/**
 * Redux-saga side effects for the casemedia (folder view) state module.
 *
 * The default export, caseMediaSaga, forks all watcher sagas for the cases/folders/files
 * tree: fetching root and sub folders (with pagination — every reconciling refetch of
 * the root list re-requests all the pages the user has loaded, never just the first,
 * because the reducer full-replaces the list at offset 0), create/rename/move/delete of
 * folders and files (including soft delete to trash — which waits for the folder's own
 * relocation into Trash to settle before the reconciling refetch, so the server cannot
 * hand the folder back under its old parent — and batch move/trash/status-update
 * of multi-selected input files), search, legacy media loading, the upload dialog's
 * read of a request's file names for its duplicate-name check (VE-30449),
 * export (download-all TDO creation and job status polling, CSV media list via Papa parse),
 * and persisting the last-open folder to localStorage per org (on selection/deletion
 * changes; cleared silently when a restore's target no longer resolves, or still resolves
 * but now sits in Trash) so a refresh can restore it.
 * Restoring the folder-tree column chain (deep links, the last-open restore, and the
 * ROUTE_HOME nav-to) also lives here: it re-attaches the target folder that Folders v2
 * omits from its own folderPath, selects nothing when the target is inside Trash,
 * and consumes the one-shot nav-to latch once the fetch settles either way.
 * caseMediaSaga is forked by initMainPage, so it lives as long as that route saga does:
 * since VE-26729 that covers the seven routes sharing initMainPage (Home, Settings
 * Profile, Redaction Codes, Statuses, Ingest, Ingest/GovQA, Trash), which no longer tear
 * each other down — an in-flight batch operation survives a switch between them, which is
 * why its beforeunload guard now lives above the router in
 * components/BatchOperationUnloadGuard rather than in the folder-view page. It is still
 * cancelled on exit to a route naming a different saga, i.e. the editors.
 * warmCaseTreeWatchers re-exposes the tree-warming subset for editor sessions, which
 * pre-load the exit's folder view (see ./caseDiscovery.ts).
 * NOT EVERYTHING HERE IS THIS SAGA'S, though: batchStatusWatchers is exported and owned
 * by the app-startup list instead, because a bulk status write has to outlive every route
 * it can be started from — see its own comment for what route ownership cost it.
 * Returning from an editor within FOLDER_VIEW_AWAY_REUSE_MS puts the request's file list
 * straight back instead of refetching it (VE-28429, re-based by VE-30022):
 * fetchCaseContentsQuery is slow on a large request, and the window now measures the TRIP —
 * how long the folder view has been away — rather than how old the list is, so sitting in a
 * request before opening a file no longer makes the trip back cost a reload. The trip
 * (`folderViewTrip`, stamped at editor route init) also names the columns that were on
 * screen, and only those may be reused. The same window covers each ancestor folder's
 * subfolder listing, which the tree restore re-selects on the way back
 * (`isSubFolderListingFresh`). `caseContentsLoadedAt`/`subFoldersLoadedAt` still record when
 * each list genuinely landed, but no longer gate anything.
 * The list itself is kept in `caseContentsRows` (see ../store) rather than read off the
 * selection, because the document editor's exit routes through the folder-tree restore,
 * which re-selects every node in the path as a bare server node and leaves neither the
 * virtual folder nor the column chain holding the request's rows. The skip never applies
 * to a first load, a fetch-more page, a folder listing, or a request whose rows are gone.
 * BOTH editors reach this gate, by different routes — the claim that used to stand here,
 * that a media-editor exit dispatches no contents fetch at all, accounted for CaseFolders
 * (which does indeed dispatch nothing when the target is already selected) but not for the
 * editor's OWN exit re-select, and was wrong (VE-30022).
 * The media editor's close button dispatches EXIT_MEDIA_DETAILS first, and that chain is
 * synchronous — REDIRECT_TO_HOME, goToMainPage, the route switcher cancelling the editor
 * task and spawning initMainPage, which forks THIS saga in its opening fork group — so by
 * the time the component re-selects the parent request, caseMediaSaga is alive again to
 * serve it: one contents fetch for the request, gated. Its OTHER exits re-select before
 * the route change, while this saga is still cancelled (and warmCaseTreeWatchers is unforked
 * on a normal open), so nothing serves them and nothing is fetched however long the session.
 * The document editor now takes the same route when it can (VE-30022): holding the request,
 * selected and renderable, it routes home with no payload and re-selects after, which costs
 * one gated contents fetch and nothing else. Only when it is NOT holding it — a cold direct
 * URL whose warm-up has not landed, a case switched inside the editor, a parent still a
 * minimal `{id}` placeholder — does it deep-link with a payload, whose navToId sends
 * CaseFolders through the folder-tree restore. That restore replays every ancestor as a bare
 * server node, which is why the rows are kept in `caseContentsRows` rather than read off the
 * selection (by then neither the virtual folder nor the chain still holds them), and why it
 * alone can refetch the ancestor COLUMNS as well as the request. It used to run on EVERY
 * document exit, including the ones that needed nothing — twice over on a direct URL, once
 * as the in-editor warm-up and once on the way out.
 * On REDACTED_FILE_COMPLETED it re-reads that file's output assets and patches its rows in
 * place (VE-28061), so a browser-side document/image burn-in that finishes while the
 * folder view is up flips the row from "Redacting" to "Redacted" without a reload; a
 * failed refresh is logged and retried once rather than silently leaving the row stale.
 * Also resolves per-folder OLP verdicts: lazily when a kebab menu opens, and eagerly for
 * the whole selected chain on every selection change (syncSelectedFolderPermissions) —
 * the gates fail closed, so always-visible controls need their verdict up front.
 * Status changes made here also write the `Redaction Status updated to: X`
 * audit event both editors write (VE-26730) — this surface wrote none, so a
 * folder-row change, and every file in a bulk change, was absent from the audit
 * log entirely. It goes through @helpers/emitTdoAuditEvent rather than
 * mediaDetails' emitter, which reads its tdoId from the OPEN editor file and
 * emits nothing when there is none.
 * Status changes made here also record a statusHistory row (VE-26730):
 * performUpdateRedactionStatus dispatches RECORD_STATUS_CHANGE itself once it has awaited
 * its own terminal action, because only the saga that issued the request can correlate it
 * with its own result — and the correction note stays in saga scope, never in the
 * mutation's GraphQL variables. The reverse direction is ensureFileStatusHistory: the
 * folder view opens no file, so nothing here had ever READ the row a correction note
 * lives on — it loads that row for one listed file on demand, after re-deriving from the
 * loaded listing that the server has already handed this user that file.
 * Dispatches actions from ../actions, reads state via ../selectors, and calls GraphQL
 * helpers in src/redact-app/state/modules/mainPage/services and the trash module sagas.
 */
import { emitTdoAuditEvent } from '@helpers/emitTdoAuditEvent';
import { selectConfigEngines } from '@common-modules/engines/selectors';
import {
  ROUTE_DOCUMENT_DETAILS,
  ROUTE_HOME,
  ROUTE_MEDIA_DETAILS,
  selectPreviousRoute,
  selectRouteType,
} from '@common-modules/routing';
import { REDACTED_FILE_COMPLETED } from '@common-modules/mediaDetails/actions';
import {
  IN_REDACTION_TAG_KEY,
  selectCaseMedia,
} from '@common-modules/mediaDetails';
import {
  getBuiltInRedactionStatusTagWriteFields,
  getRedactionStatusIdentity,
  mergeRedactionStatusTagFields,
  resolveRedactionStatusDisplayLabel,
} from '@common-modules/mediaDetails/redactionStatuses';
// Deep imports (not the statusHistory barrel): the barrel pulls that module's
// sagas, which import this module's actions — a cycle (VE-26730).
import {
  actionFetchLatestStatusRow,
  actionMarkTdoReadVerified,
  actionRecordStatusHistory,
} from '@common-modules/statusHistory/actions';
import {
  deriveStatusFieldsFromSelection,
  deriveStatusFieldsFromTag,
} from '@common-modules/statusHistory/models';
import {
  selectLatestStatusRow,
  selectStatusHistoryGate,
} from '@common-modules/statusHistory/selectors';
import type { StatusHistoryStore } from '@common-modules/statusHistory/store';
import { selectRedactionCustomStatuses } from '@redact-modules/redactionStatuses/selectors';
import { closeSnackbar, enqueueSnackbar } from '@common-modules/snackbar';
import {
  CaseId,
  FolderId,
  TDOId,
  TreeObjectId,
} from '@common/state/modules/universal/models/Brands';
import { selectOrganizationName } from '@common/user-onboarding';
import {
  selectOrganizationId,
  selectV2FoldersEnabled,
} from '@common/user-onboarding/selectors/organization';
import {
  selectRootFolderSmartSelection,
  selectTrashRetentionDays,
} from '@common/user-permissions';
import { LOCK_REFRESH } from '@helpers/constants';
import { selectUser } from '@helpers/tdoHelper';
import { sagaIntl } from '@i18n';
import {
  actionClearFileMultiSelect,
  actionClearNavCase,
  actionClearPendingRemovalFiles,
  actionCreateNewFolder,
  actionCreateRootFolder,
  actionDeleteFile,
  actionDeleteMediaFileSdo,
  actionDownloadAllJobStatusRequest,
  actionDownloadAllStatus,
  actionExportFilesRequest,
  actionExportMediaListRequest,
  actionFetchAllLegacyMedias,
  SET_UNASSIGNED_SORT,
  actionSetUnassignedSort,
  actionFetchExportTdoRequest,
  actionFetchFolderRequest,
  actionReleasePendingTrashFolders,
  actionFetchForExpandedFolders,
  actionFetchTdoDetailForSdos,
  actionMoveFolderRequest,
  actionReleasePendingRemovalFiles,
  actionRemoveCaseInputFiles,
  actionRenameFolder,
  actionSearchNameExistsInFolderRequest,
  actionSetBatchOperation,
  actionSetPendingRemovalFiles,
  actionSetRootFolderSmartSelection,
  actionSetSelectedFolder,
  actionSetSelectedFolderSuccess,
  actionUpdateRedactionStatusChange,
  BATCH_APPLY_WATERMARK_REQUEST,
  BATCH_MOVE_FILES_REQUEST,
  BATCH_TRASH_FILES_REQUEST,
  BATCH_UPDATE_STATUS_REQUEST,
  CREATE_EXPORT_TDO_FAILURE,
  CREATE_EXPORT_TDO_INIT,
  CREATE_EXPORT_TDO_REQUEST,
  CREATE_EXPORT_TDO_SUCCESS,
  CREATE_NEW_FOLDER,
  CREATE_NEW_FOLDER_FAILURE,
  CREATE_NEW_FOLDER_SUCCESS,
  CREATE_ROOT_FOLDER_FAILURE,
  CREATE_ROOT_FOLDER_INIT,
  CREATE_ROOT_FOLDER_REQUEST,
  CREATE_ROOT_FOLDER_SUCCESS,
  DELETE_CASE_SDO_FAILURE,
  DELETE_CASE_SDO_REQUEST,
  DELETE_FILE_FAILURE,
  DELETE_FILE_INIT,
  DELETE_FILE_RELOCATED,
  DELETE_FILE_REQUEST,
  DELETE_FILE_SUCCESS,
  DELETE_FOLDER_FAILURE,
  DELETE_FOLDER_INIT,
  DELETE_FOLDER_REQUEST,
  DELETE_FOLDER_SUCCESS,
  DELETE_MEDIA_FILE_SDO_FAILURE,
  DELETE_MEDIA_FILE_SDO_REQUEST,
  DELETE_MEDIA_FILE_SDO_SUCCESS,
  EXPORT_FILES_FAILURE,
  EXPORT_FILES_INIT,
  EXPORT_FILES_REQUEST,
  EXPORT_FILES_SUCCESS,
  EXPORT_MEDIA_LIST_FAILURE,
  EXPORT_MEDIA_LIST_REQUEST,
  EXPORT_MEDIA_LIST_SUCCESS,
  FETCH_DOWNLOADALL_JOB_STATUS_FAILURE,
  FETCH_DOWNLOADALL_JOB_STATUS_INIT,
  FETCH_DOWNLOADALL_JOB_STATUS_REQUEST,
  FETCH_DOWNLOADALL_JOB_STATUS_SUCCESS,
  FETCH_EXPORT_TDO_FAILURE,
  FETCH_EXPORT_TDO_INIT,
  FETCH_EXPORT_TDO_REQUEST,
  FETCH_EXPORT_TDO_SUCCESS,
  FETCH_FOR_EXPANDED_FOLDER_FAILURE,
  FETCH_FOR_EXPANDED_FOLDER_INIT,
  FETCH_FOR_EXPANDED_FOLDER_REQUEST,
  FETCH_FOR_EXPANDED_FOLDER_SUCCESS,
  FETCH_LEGACY_MEDIAS_FAILURE,
  FETCH_LEGACY_MEDIAS_REQUEST,
  FETCH_LEGACY_MEDIAS_SUCCESS,
  FETCH_MORE_FILES_FAILURE,
  FETCH_MORE_FILES_INIT,
  FETCH_MORE_FILES_REQUEST,
  FETCH_MORE_FILES_SUCCESS,
  FETCH_MORE_ROOTFOLDERS_FAILURE,
  FETCH_MORE_ROOTFOLDERS_INIT,
  FETCH_MORE_ROOTFOLDERS_REQUEST,
  FETCH_MORE_ROOTFOLDERS_SUCCESS,
  FETCH_MORE_SUBFOLDERS_FAILURE,
  FETCH_MORE_SUBFOLDERS_INIT,
  FETCH_MORE_SUBFOLDERS_REQUEST,
  FETCH_MORE_SUBFOLDERS_SUCCESS,
  FETCH_FOLDER_PERMISSIONS_REQUEST,
  FETCH_ROOT_FOLDERS_FAILURE,
  FETCH_ROOT_FOLDERS_INIT,
  FETCH_ROOT_FOLDERS_REQUEST,
  FETCH_ROOT_FOLDERS_SUCCESS,
  FETCH_SELECTED_CASE_OR_FOLDER_TREE_FAILURE,
  FETCH_SELECTED_CASE_OR_FOLDER_TREE_INIT,
  FETCH_SELECTED_CASE_OR_FOLDER_TREE_REQUEST,
  FETCH_SELECTED_CASE_OR_FOLDER_TREE_SUCCESS,
  FETCH_SUB_FOLDERS_BYID_FAILURE,
  FETCH_SUB_FOLDERS_BYID_REQUEST,
  FETCH_SUB_FOLDERS_BYID_SUCCESS,
  FETCH_TDO_DETAIL_FOR_SDOS_FAILURE,
  FETCH_TDO_DETAIL_FOR_SDOS_REQUEST,
  FILE_TDO_WITH_CASE_FAILURE,
  FILE_TDO_WITH_CASE_INIT,
  FILE_TDO_WITH_CASE_REQUEST,
  FILE_TDO_WITH_CASE_SUCCESS,
  MOVE_FILE_TO_CASE_FAILURE,
  MOVE_FILE_TO_CASE_INIT,
  MOVE_FILE_TO_CASE_REQUEST,
  MOVE_FILE_TO_CASE_SUCCESS,
  MOVE_FOLDER_FAILURE,
  MOVE_FOLDER_INIT,
  MOVE_FOLDER_REQUEST,
  MOVE_FOLDER_SUCCESS,
  REMOVE_SELECTED_FOLDER,
  RENAME_FOLDER_FAILURE,
  RENAME_FOLDER_INIT,
  RENAME_FOLDER_REQUEST,
  RENAME_FOLDER_SUCCESS,
  SEARCH_CASES_AND_FOLDERS,
  SEARCH_CASES_AND_FOLDERS_FAILURE,
  SEARCH_NAME_EXISTS_IN_FOLDER_FAILURE,
  SEARCH_NAME_EXISTS_IN_FOLDER_INIT,
  SEARCH_NAME_EXISTS_IN_FOLDER_REQUEST,
  SEARCH_NAME_EXISTS_IN_FOLDER_SUCCESS,
  SET_SELECTED_FOLDER_REQUEST,
  SET_SELECTED_FOLDER_SUCCESS,
  SOFT_DELETE_CASE_SDO_REQUEST,
  SOFT_DELETE_FOLDER_FAILURE,
  SOFT_DELETE_FOLDER_REQUEST,
  SOFT_DELETE_FOLDER_SUCCESS,
  TDO_IN_USE,
  UPDATE_REDACTION_STATUS_FAILURE,
  UPDATE_REDACTION_STATUS_INIT,
  ENSURE_FILE_STATUS_HISTORY,
  FETCH_REQUEST_ROSTER_REQUEST,
  FETCH_CASE_FILE_ROWS_REQUEST,
  actionPublishRequestRoster,
  actionFetchRequestRoster,
  UPDATE_REDACTION_STATUS_REQUEST,
  UPDATE_REDACTION_STATUS_SUCCESS,
  REFRESH_FILE_OUTPUTS_FAILURE,
  REFRESH_FILE_OUTPUTS_REQUEST,
  REFRESH_FILE_OUTPUTS_SUCCESS,
  actionRefreshFileOutputs,
} from '@redact-modules/casemedia/actions';
import {
  canReuseAfterTrip,
  MULTI_SELECT_EXIT_ANIMATION_MS,
} from '@redact-modules/casemedia/contants';
import {
  Action,
  ActionBatchApplyWatermarkPayload,
  ActionBatchMoveFilesPayload,
  ActionBatchTrashFilesPayload,
  ActionBatchUpdateStatusPayload,
  ActionCreateNewFolderPayload,
  ActionDeleteCaseSdoPayload,
  ActionDeleteFilePayload,
  ActionDeleteMediaFileSdoPayload,
  ActionFetchFolderRequest,
  ActionFetchForExportedFolderPayload,
  ActionFetchLegacyMediasRequest,
  ActionFetchMoreFilesRequest,
  ActionFetchSubFoldersPayload,
  ActionSetSelectedFolderPayload,
  ActionFileTdoWithCasePayload,
  ActionLegacyMediasSuccessPayload,
  ActionMoveFileToCasePayload,
  ActionMoveFolderPayload,
  ActionRenameFolderPayload,
  ActionRestoredFolderPayload,
  ActionSoftDeleteCaseSdoPayload,
  ActionUpdateRedactionStatusRequestPayload,
  CreateNewFolderPayload,
  DownloadAllJobStatusRequest,
  ExportFilesRequest,
  ExportMediaListFields,
  ExportTdoRequest,
  FetchRootFolderRequest,
  FetchSubfolderRequest,
  isRedactionStatus,
  MoveFolderRequest,
  SearchNameActions,
  SearchNameExistsInFolderRequestPayload,
} from '@redact-modules/casemedia/models';
import {
  selectDefaultLimit,
  selectDefaultOffset,
  selectAllExpandedFolders,
  selectLegacyMedias,
  selectMultiSelectCaseId,
  selectRequestRoster,
  selectUnassignedSort,
  selectRootFolder,
  selectRootFolderId,
  selectRootFoldersOffset,
  selectFolderViewTrip,
  selectSelectedFolderIds,
  selectSelectedVirtualFolder,
  selectSelectedFolders,
  selectSelectedOutputFiles,
  selectSelectedTdoFile,
  selectIsFolderConfirmedViewOnly,
} from '@redact-modules/casemedia/selectors';
import * as Services from '@redact-modules/casemedia/services';
import {
  createExportFilesQuery,
  CreateExportFilesQueryResponse,
  createRootFoldersQuery,
  CreateRootFoldersQueryResponse,
  deleteFileQuery,
  DeleteFileQueryResponse,
  deleteFolderOrCaseQuery,
  DeleteFolderOrCaseQueryResponse,
  deleteOutputFileQuery,
  DeleteOutputFileQueryResponse,
  exportFilesQuery,
  ExportFilesQueryResponse,
  fetchCaseContentsForExportQuery,
  FetchCaseContentsForExportQueryResponse,
  fetchCaseContentsQuery,
  FetchCaseContentsQueryResponse,
  fetchCaseOrFolderTreeQuery,
  FetchCaseOrFolderTreeQueryResponse,
  fetchExportTdoQuery,
  FetchExportTdoQueryResponse,
  fetchJobStatusQuery,
  FetchJobStatusQueryResponse,
  fetchSubFoldersForExportQuery,
  FetchSubFoldersForExportQueryResponse,
  fetchSubFoldersQuery,
  FetchSubFoldersQueryResponse,
  fileTdoWithCaseQuery,
  FileTdoWithCaseQueryResponse,
  moveFolderOrCaseQuery,
  MoveFolderOrCaseQueryResponse,
  moveTdoQuery,
  MoveTdoQueryResponse,
  renameFolderQuery,
  RenameFolderQueryResponse,
  searchNameExistInFolderQuery,
  SearchNameExistInFolderQueryResponse,
  softDeleteFileQuery,
  SoftDeleteFileQueryResponse,
  updateRedactionStatusQuery,
  UpdateRedactionStatusQueryResponse,
} from '@redact-modules/casemedia/services/queries';
import {
  AssetFileType,
  FolderStateType,
  isFolderInTrash,
  LegacyMediaDetailType,
  LegacyMediaFileDataType,
  assetBelongsToFile,
  persistLastOpenFolder,
  readStoredLastOpenFolder,
  VirtualFolderNames,
  defaultState,
} from '@redact-modules/casemedia/store';
import {
  isUnassignedFile,
  isUnassignedFolderId,
} from '@redact-modules/casemedia/unassigned';
import {
  isKnownLargeRequest,
  rememberLargeRequest,
} from '@redact-modules/casemedia/largeRequestCache';
import { isRosterOnlyRow } from '@redact-modules/casemedia/rosterRow';
import { rosterEntriesFrom } from '@redact-modules/casemedia/services';
import { renderLockRefusal } from '@redact-modules/casemedia/lockRefusalMessage';
import {
  findDuration,
  FolderPathItem,
  formatExportDateTime,
  isExportImageTdo,
  isExportPdfTdo,
  resolveFolderMutationId,
  sortFolderPath,
  tdoFolderChain,
  folderChainToPath,
} from '@redact-modules/helper';
import {
  serviceFindWatermarkImageAsset,
  serviceLoadDocumentRedactionData,
  serviceSaveDocumentRedactionData,
  serviceSaveWatermarkImageAsset,
} from '@redact-modules/documents/services';
import {
  appendWatermarksToAssetBody,
  watermarksFromAssetBody,
} from '@redact-modules/documents/helpers';
import type { AppliedWatermark } from '@redact-modules/documents/models';
import { resolveWatermarkRaster } from '@redact-modules/watermarks/artwork';
import { selectAllWatermarkRecords } from '@redact-modules/watermarks/selectors';
import { DOCUMENT_REDACTION_ASSET_FORMAT_VERSION } from '@redact-modules/documents/store';
import {
  selectToken as selectDocToken,
  selectGraphQLConfig as selectDocGraphQLConfig,
} from '@redact-modules/documents/sagas/auth';
import * as MainPageServices from '@redact-modules/mainPage/services';
import {
  actionUpdateSelectedFolderItems,
  UPDATE_RESTORED_FOLDER_NAME_FAILURE,
  UPDATE_RESTORED_FOLDER_NAME_SUCCESS,
} from '@redact-modules/trash/actions';
import { ensureTrashFolderId } from '@redact-modules/trash/sagas';
import { selectFolderSelectedItems } from '@redact-modules/trash/selectors';
import { downloadNow } from '@redact/state/modules/casemedia/helper';
import { isEmpty } from 'lodash';
import _sortBy from 'lodash/sortBy';
import moment from 'moment';
import { purgeTdoSideData } from '@redact-modules/trash/sagas/purgeTdoSideData';
import Papa from 'papaparse';
import {
  all,
  call,
  cancelled,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';

export const defaultSearchNameInFolderLimit = 30;
export const defaultSearchNameInFolderOffset = 0;

type TDOForExport = MainPageServices.TDOForExport;

export interface ExportRowParams {
  item: TDOForExport;
  fields: ExportMediaListFields;
  isMainPage: boolean;
  orgName?: string;
  isUnassignedMedia?: boolean;
  isV2Folders?: boolean; // VE-21114 — see tdoFolderChain
}

export interface ExportMediaListRow {
  'Root Folder'?: string;
  Folder?: string;
  'Sub Folder'?: string;
  Request?: string;
  Filename?: string;
  'File Id'?: string;
  Duration?: string;
  'Created Date'?: string;
  'Modified Date'?: string;
  Status?: string;
  'Redaction Name'?: string;
  'Has Audio Redactions'?: string;
  'Has Visual Redactions'?: string;
}

/* Root sagas */
export default function* caseMediaSaga() {
  yield* all([
    fork(watchCreateRootFolder),
    fork(watchFetchRootFolders),
    fork(watchCreateNewFolders),
    fork(watchSetSelectedFolder),
    fork(watchDeleteFolder),
    fork(watchSoftDeleteFolder),
    fork(watchDeleteFiles),
    fork(watchExportFiles),
    fork(watchFetchMoreFolders),
    fork(watchMoveFolder),
    fork(watchFetchCase),
    fork(watchSearchCasesFolders),
    fork(watchFetchMoreFiles),
    fork(watchFetchLegacyMedias),
    fork(watchFileTdoWithCase),
    fork(watchDeleteMediaFileSdo),
    fork(watchFetchTdoDetailForSdos),
    fork(watchRenameFolderOrCase),
    fork(watchExpandedFolderRequests),
    fork(watchSearchNameExists),
    fork(watchMoveFileToCase),
    fork(watchTdoInUse),
    fork(watchExportMediaList),
    fork(watchUpdateRedactionStatus),
    fork(watchRefreshFileOutputs),
    fork(watchRequestRoster),
    fork(watchCaseFileRows),
  ]);

  // VE-28429: pay the returned-file refresh directly, because the taker above
  // cannot see the action it is written for. `watchRefreshFileOutputs` registers
  // `takeEvery(ROUTE_HOME, ...)`, but THIS saga is forked by initMainPage, which
  // the router spawns in response to that very ROUTE_HOME — redux-saga snapshots
  // its takers before emitting, so a taker registered during the emit misses it.
  // mainPage documents the same trap above its own fork list and pays an owed
  // refresh for it. Without this the refresh is dead on the editor→folder-view
  // trip, which is the only trip it exists for: the freshness skip suppresses the
  // folder refetch and nothing else brings the edited row up to date.
  // A `takeEvery(ROUTE_HOME, ...)` alongside it would be pure decoration and is
  // deliberately absent: the only ROUTE_HOME it could ever observe is one that
  // arrives while this saga is already alive, i.e. from inside the preserved
  // group (Settings, Codes, Statuses, Ingest, Trash) — and for all of those the
  // previous route is not an editor, so the handler no-ops. One mechanism, and
  // it is this call.
  //
  // Gated on actually landing on Home: initMainPage serves seven routes, so
  // without this, leaving an editor for Settings or Trash would fire a TDO query
  // to patch a row that is not on screen.
  const routeType = yield* select(selectRouteType);
  if (routeType === ROUTE_HOME.type) {
    yield* call(refreshReturnedEditorFile);
  }
}

/**
 * THE WHOLE CHAIN A BATCH STATUS WRITE NEEDS, owned at app startup.
 *
 * Not three takers that happen to be listed together: the request watcher
 * performs each file's mutation, SUCCESS turns that into the optimistic row
 * update every surface reads, and FAILURE is the only thing that makes a
 * refused write visible. Forking the request alone made the server change and
 * the screen not — worse than doing nothing, because it survives a reload.
 *
 * NOT A ROUTE SAGA, and this is the point. It lived in `caseMediaSaga`, which
 * `initMainPage` forks, so from an editor it was dead — the routing saga
 * cancels the previous route task on every route action. Re-forking it inside
 * `initDocumentEditor` fixed the dead watcher and bought a worse bug: that
 * task is cancelled on re-entry too, and the editor re-enters its own route
 * whenever the open document changes. Switching document mid-batch cancelled
 * the loop with some files written and some not, no summary toast, no error —
 * and if the cancel landed inside `performUpdateRedactionStatus`'s outcome
 * race after the server had accepted the mutation, that file's audit event and
 * status-history row never ran. A status change in a public-records release
 * with nothing in its trail.
 *
 * So it belongs where the other long, audited, non-transactional writes
 * already live — its own entry in the app-startup list beside `redactAllSaga`,
 * `combineProjectSaga` and `documentBurnInSaga`, for the reason their comments
 * give: one owner, app lifetime, its own restart boundary, and no route can
 * take it away mid-release.
 */
export function* batchStatusWatchers() {
  yield* all([
    takeLatest(BATCH_UPDATE_STATUS_REQUEST, batchUpdateStatusRequest),
    takeEvery(UPDATE_REDACTION_STATUS_SUCCESS, updateRedactionStatusSuccess),
    takeEvery(UPDATE_REDACTION_STATUS_FAILURE, updateRedactionStatusFailure),
  ]);
}

// VE-25873 Unit 3 — the minimal watcher set needed to warm the folder-view
// column tree (root folders → case/folder chain → each column's contents)
// while an EDITOR route is active. caseMediaSaga above is forked by initMainPage
// and the routing saga cancels it when a route names a different saga — which the
// editor routes do — so during an editor session these request actions would
// otherwise go unhandled.
// caseDiscovery.ts forks this inside each editor's route saga, so it lives
// exactly as long as the session; the failure watchers are deliberately left
// out — a failed warm-up must stay silent in the editor (the folder view's
// fallback deep-link reports its own errors on the Home route).
export function* warmCaseTreeWatchers() {
  yield* all([
    takeLatest(FETCH_ROOT_FOLDERS_REQUEST, fetchRootFolders),
    takeLatest(
      FETCH_SELECTED_CASE_OR_FOLDER_TREE_REQUEST,
      fetchSelectedCaseOrFolderTreeRequest
    ),
    takeLatest(
      FETCH_SELECTED_CASE_OR_FOLDER_TREE_SUCCESS,
      fetchSelectedCaseOrFolderTreeSuccess
    ),
    takeLatest(SET_SELECTED_FOLDER_REQUEST, setSelectedFolder),
    takeLatest(SET_SELECTED_FOLDER_SUCCESS, fetchSubFolders),
    takeLatest(SET_SELECTED_FOLDER_SUCCESS, fetchRosterForSelectedFolder),
    takeLatest(FETCH_SUB_FOLDERS_BYID_SUCCESS, rosterFromFirstPage),
    // VE-32420: and the handlers those two DISPATCH TO. Both can `put`
    // FETCH_REQUEST_ROSTER_REQUEST, whose watcher lives in `caseMediaSaga` —
    // cancelled for the whole editor session. Without these, deep-linking into
    // the editor for a file in a request of more than one page left the roster
    // stuck at `status: 'loading'` forever and the Documents panel spinning,
    // because the request action was dispatched and nothing served it. A
    // short-page request was unaffected, which is why it hid in small requests.
    takeEvery(FETCH_REQUEST_ROSTER_REQUEST, fetchRequestRoster),
    takeLatest(FETCH_CASE_FILE_ROWS_REQUEST, fetchCaseFileRows),
    takeLatest(SET_SELECTED_FOLDER_REQUEST, persistLastOpenFolderFromState),
  ]);
}

/* Watcher Sagas */
function* watchFetchRootFolders() {
  yield* all([
    takeLatest(FETCH_ROOT_FOLDERS_REQUEST, fetchRootFolders),
    takeEvery(FETCH_FOLDER_PERMISSIONS_REQUEST, fetchFolderPermissions),
    takeLatest(FETCH_ROOT_FOLDERS_SUCCESS, fetchRootFoldersSuccess),
    takeLatest(FETCH_MORE_ROOTFOLDERS_SUCCESS, fetchRootFoldersSuccess),
    takeLatest(FETCH_ROOT_FOLDERS_FAILURE, fetchRootFoldersFailure),
  ]);
}
function* watchCreateRootFolder() {
  yield* all([
    takeLatest(CREATE_ROOT_FOLDER_REQUEST, createRootFolder),
    takeLatest(CREATE_ROOT_FOLDER_SUCCESS, createRootFolderSuccess),
    takeLatest(CREATE_ROOT_FOLDER_FAILURE, createRootFolderFailure),
  ]);
}

function* watchFetchMoreFolders() {
  yield* all([
    takeLatest(FETCH_MORE_ROOTFOLDERS_REQUEST, fetchMoreRootFolderRequest),
    takeLatest(FETCH_MORE_SUBFOLDERS_REQUEST, fetchMoreSubFoldersRequest),
  ]);
}

// The OLP gates are fail-closed, so anything gated on a folder stays disabled
// until a check has confirmed it. The kebab menu can fetch on open, but the
// header buttons (Add Folder / Add Request / Upload Media) and the per-file
// status pill are always on screen — with a lazy fetch they would sit disabled
// forever in an OLP org. Refresh the verdicts for the whole selected chain
// whenever the selection changes; fetchFolderPermissions skips ids it already
// holds and no-ops entirely off OLP, so this is cheap and safe to over-call.
export function* syncSelectedFolderPermissions() {
  const folderIds = yield* select(selectSelectedFolderIds);
  // VE-26943: the Unassigned Media sentinel is not an aiWARE folder id — asking
  // for its permissions would fail the whole batch.
  const known = folderIds.filter(
    (id): id is FolderId => Boolean(id) && !isUnassignedFolderId(id)
  );
  if (known.length) {
    yield* put(FETCH_FOLDER_PERMISSIONS_REQUEST(known));
  }
}

function* watchSetSelectedFolder() {
  yield* all([
    takeLatest(SET_SELECTED_FOLDER_REQUEST, setSelectedFolder),
    takeLatest(SET_SELECTED_FOLDER_SUCCESS, fetchSubFolders),
    takeLatest(SET_SELECTED_FOLDER_SUCCESS, fetchRosterForSelectedFolder),
    takeLatest(FETCH_SUB_FOLDERS_BYID_SUCCESS, rosterFromFirstPage),
    takeLatest(SET_SELECTED_FOLDER_SUCCESS, syncSelectedFolderPermissions),
    takeLatest(REMOVE_SELECTED_FOLDER, syncSelectedFolderPermissions),
    takeLatest(FETCH_SUB_FOLDERS_BYID_FAILURE, fetchSubFoldersFailure),
    // Runs after the reducer has applied the (de)selection, so state holds
    // the new deepest column. Deletions are watched too: their reducers trim
    // the deleted folder out of selectedFolders directly, and without a
    // re-persist the stored id would keep pointing at the deleted/trashed
    // folder and restore into it on the next refresh.
    takeLatest(SET_SELECTED_FOLDER_REQUEST, persistLastOpenFolderFromState),
    takeLatest(REMOVE_SELECTED_FOLDER, persistLastOpenFolderFromState),
    takeLatest(DELETE_FOLDER_SUCCESS, persistLastOpenFolderFromState),
    takeLatest(SOFT_DELETE_FOLDER_SUCCESS, persistLastOpenFolderFromState),
  ]);
}

// Remember the deepest open folder/request so a page refresh can restore it
// (CaseFolders reads it back on mount and re-selects via
// fetchSelectedCaseOrFolderTree). Cleared when the user is back at root.
// isCase comes from contentTemplates (a request always carries one) — NOT
// checkIsCaseId, whose `caseFiles` marker only exists after a files fetch
// has populated the state entry, i.e. not at selection time.
export function* persistLastOpenFolderFromState() {
  const orgId = yield* select(selectOrganizationId);
  const folders = yield* select(selectSelectedFolders);
  const last = folders?.at(-1);
  persistLastOpenFolder(
    orgId,
    last?.id
      ? {
          id: last.id,
          isCase: !!(last.contentTemplates && last.contentTemplates.length > 0),
        }
      : null
  );
}

function* watchDeleteFolder() {
  yield* all([
    takeLatest(DELETE_CASE_SDO_REQUEST, deleteCaseSdoRequest),
    takeLatest(DELETE_CASE_SDO_FAILURE, deleteCaseSdoFailure),
    takeLatest(DELETE_FOLDER_REQUEST, deleteFolderRequest),
    takeLatest(DELETE_FOLDER_SUCCESS, deleteFolderSuccess),
    takeLatest(DELETE_FOLDER_FAILURE, deleteFolderFailure),
  ]);
}

function* watchSoftDeleteFolder() {
  yield* all([
    takeLatest(SOFT_DELETE_CASE_SDO_REQUEST, softDeleteCaseSdoRequest),
    takeLatest(SOFT_DELETE_FOLDER_REQUEST, softDeleteFolderRequest),
    // VE-26484: takeEvery, not takeLatest — the worker now parks in a race
    // waiting for its own folder's move outcome. takeLatest would cancel a
    // parked worker when a second folder is trashed, losing its reconciling
    // re-fetch; each worker matches only its own treeObjectId, so they coexist.
    takeEvery(SOFT_DELETE_FOLDER_SUCCESS, softDeleteFolderSuccess),
    takeLatest(SOFT_DELETE_FOLDER_FAILURE, softDeleteFolderFailure),
  ]);
}

export const watchDeleteFiles = function* () {
  yield* all([
    takeLatest(DELETE_FILE_REQUEST, deleteFileRequest),
    takeLatest(BATCH_TRASH_FILES_REQUEST, batchTrashFilesRequest),
    takeLatest(BATCH_MOVE_FILES_REQUEST, batchMoveFilesRequest),
    takeLatest(DELETE_FILE_SUCCESS, deleteFileSuccess), // TODO: Fix need for type
    takeLatest(DELETE_FILE_FAILURE, deleteFileFailure),
  ]);
};

function* watchExportFiles() {
  yield* all([
    takeLatest(CREATE_EXPORT_TDO_REQUEST, createExportTdoRequest),
    takeLatest(CREATE_EXPORT_TDO_SUCCESS, createExportTdoSuccess),
    // TODO: Fix these two
    takeLatest(FETCH_EXPORT_TDO_REQUEST, fetchExportTdoRequest),
    takeLatest(FETCH_EXPORT_TDO_SUCCESS, fetchExportTdoSuccess),
    takeLatest(EXPORT_FILES_REQUEST, exportFilesRequest),
    takeLatest(EXPORT_FILES_SUCCESS, exportFilesSuccess),
    takeLatest(
      FETCH_DOWNLOADALL_JOB_STATUS_REQUEST,
      downloadAllJobStatusRequest
    ),
    takeLatest(
      FETCH_DOWNLOADALL_JOB_STATUS_SUCCESS,
      downloadAllJobStatusSuccess
    ),
  ]);
}

function* watchCreateNewFolders() {
  yield* all([
    takeLatest(CREATE_NEW_FOLDER, createNewFolder),
    takeLatest(CREATE_NEW_FOLDER_SUCCESS, createNewFolderSuccess),
    takeLatest(CREATE_NEW_FOLDER_FAILURE, createNewFolderFailure),
  ]);
}

function* watchMoveFolder() {
  yield* all([
    takeLatest(MOVE_FOLDER_REQUEST, moveFolderRequest),
    takeLatest(MOVE_FOLDER_SUCCESS, moveFolderSuccess),
    takeLatest(MOVE_FOLDER_FAILURE, moveFolderFailure),
  ]);
}

function* watchFetchCase() {
  yield* all([
    takeLatest(
      FETCH_SELECTED_CASE_OR_FOLDER_TREE_REQUEST,
      fetchSelectedCaseOrFolderTreeRequest
    ),
    takeLatest(
      FETCH_SELECTED_CASE_OR_FOLDER_TREE_SUCCESS,
      fetchSelectedCaseOrFolderTreeSuccess
    ),
    takeLatest(
      FETCH_SELECTED_CASE_OR_FOLDER_TREE_FAILURE,
      fetchSelectedCaseOrFolderTreeFailure
    ),
  ]);
}

function* watchSearchCasesFolders() {
  yield* all([
    takeLatest(SEARCH_CASES_AND_FOLDERS, function* () {
      yield* put(Services.fetchCasesFolderSearch({}));
    }),
    takeLatest(SEARCH_CASES_AND_FOLDERS_FAILURE, searchCasesFoldersFailHandler),
  ]);
}

function* watchFetchMoreFiles() {
  yield* all([
    takeLatest(FETCH_MORE_FILES_REQUEST, fetchMoreFilesRequest),
    takeLatest(FETCH_MORE_FILES_FAILURE, fetchMoreFilesFailure),
  ]);
}

/**
 * VE-32420: the sort changed, so the SDO list has to be read again.
 *
 * Unassigned Media's sort is the server's ordering, not a client pass — so
 * changing it is not a re-sort of what is held but a different read. From
 * OFFSET 0, which the success reducer treats as a replace rather than an
 * append: the pages already loaded are slices of the previous ordering and
 * mean nothing under the new one.
 */
function* refetchLegacyMediasForSort() {
  const limit = yield* select(selectDefaultLimit);
  yield* put(actionFetchAllLegacyMedias({ limit, offset: 0 }));
}

/**
 * VE-32420: leaving Unassigned Media puts its sort back to the default.
 *
 * The sort is a choice made WHILE BROWSING that column, and the column is not
 * the only thing showing that list: once the user selects another folder the
 * list becomes the docked panel on the left, which renders it straight from
 * state with no sort control of its own. Carrying the choice over would leave
 * the dock ordered by something chosen elsewhere, for a panel whose whole job
 * is a predictable glance.
 *
 * The sort was already session-only (it is not persisted); this just shortens
 * "session" to "while the column is open". Dispatching it re-reads the list
 * from offset 0, which is what actually re-orders the dock, so it is skipped
 * when the sort is already the default.
 *
 * Nothing is done to `filters` here: they never reach the dock, which maps
 * `allLegacyMedias` directly rather than going through the column's filtered
 * list — and they are shared with every other column, so resetting them on
 * this transition would clear a filter set on the request being opened.
 */
export function* resetUnassignedSortOnLeave(
  action: ReturnType<typeof actionSetSelectedFolderSuccess>
) {
  const { folderId } = action.payload;
  if (!folderId || isUnassignedFolderId(folderId)) {
    return;
  }
  const sort = yield* select(selectUnassignedSort);
  if (sort === defaultState.unassignedSort) {
    return;
  }
  yield* put(actionSetUnassignedSort(defaultState.unassignedSort));
}

function* watchFetchLegacyMedias() {
  yield* all([
    takeLatest(SET_UNASSIGNED_SORT, refetchLegacyMediasForSort),
    takeLatest(SET_SELECTED_FOLDER_SUCCESS, resetUnassignedSortOnLeave),
    takeLatest(FETCH_LEGACY_MEDIAS_REQUEST, fetchLegacyMediasRequest),
    // VE-26943: takeEvery, not takeLatest. Pages are now user-driven (scroll),
    // so two can land close together; takeLatest would cancel the first page's
    // handler and lose its TDO-enrichment dispatch.
    takeEvery(FETCH_LEGACY_MEDIAS_SUCCESS, fetchLegacyMediasSuccess),
    takeLatest(FETCH_LEGACY_MEDIAS_FAILURE, fetchLegacyMediasFailure),
  ]);
}

function* watchFileTdoWithCase() {
  yield* all([
    takeLatest(FILE_TDO_WITH_CASE_REQUEST, fileTdoWithCaseRequest),
    takeLatest(FILE_TDO_WITH_CASE_SUCCESS, fileTdoWithCaseSuccess), // TODO: Fix this
    takeLatest(FILE_TDO_WITH_CASE_FAILURE, fileTdoWithCaseFailure),
  ]);
}

function* watchDeleteMediaFileSdo() {
  yield* all([
    takeLatest(DELETE_MEDIA_FILE_SDO_REQUEST, deleteMediaFileSdoRequest),
    takeLatest(DELETE_MEDIA_FILE_SDO_SUCCESS, deleteMediaFileSdoSuccess),
    takeLatest(DELETE_MEDIA_FILE_SDO_FAILURE, deleteMediaFileSdoFailure),
  ]);
}

function* watchFetchTdoDetailForSdos() {
  yield* all([
    takeLatest(FETCH_TDO_DETAIL_FOR_SDOS_REQUEST, fetchTdoDetailForSdosRequest),
    takeLatest(FETCH_TDO_DETAIL_FOR_SDOS_FAILURE, fetchTdoDetailForSdosFailure),
  ]);
}

// VE-28061: a folder row's Redacted/Original pill reads `redactedMediaAssets`
// as loaded with the folder, and nothing else refreshes it. A browser-side
// document (or image) burn-in that finishes while the folder view is up would
// therefore flip from "Redacting" to a stale "Original". REDACTED_FILE_COMPLETED
// is the app-wide "a redaction finished" event, so re-read that file's outputs
// on it. Only alive on the Home route (like the rest of caseMediaSaga) — which is
// exactly where a row is on screen to be wrong.
function* watchRefreshFileOutputs() {
  yield* all([
    takeEvery(REDACTED_FILE_COMPLETED, refreshFileOutputsOnRedactionComplete),
    takeEvery(REFRESH_FILE_OUTPUTS_REQUEST, refreshFileOutputsRequest),
    takeEvery(REFRESH_FILE_OUTPUTS_SUCCESS, clearRefreshFileOutputsRetry),
    takeEvery(REFRESH_FILE_OUTPUTS_FAILURE, refreshFileOutputsFailure),
  ]);
}

/**
 * VE-28429: bring the one file the user was just editing up to date on the way
 * back into the folder view.
 *
 * The counterpart to the re-select skip: within the freshness window the folder
 * is NOT refetched, so without this the row for the file just closed would keep
 * whatever it showed when the folder loaded — the one row on screen guaranteed
 * to be worth re-reading, since the editor is where it changes. One aliased TDO
 * instead of the whole request.
 *
 * Runs on every return, not only the skipped ones: when the folder IS refetched
 * this is redundant but harmless (same patch, arriving first), and making it
 * conditional would couple it to the skip's four conditions for no gain.
 *
 * Mirrors mainPage's `onReturnFromMediaDetailsHandle`, which does the same for
 * the legacy dashboard.
 */
export function* refreshReturnedEditorFile() {
  const previousRoute = yield* select(selectPreviousRoute);
  const cameFromEditor =
    previousRoute?.type === ROUTE_MEDIA_DETAILS.type ||
    previousRoute?.type === ROUTE_DOCUMENT_DETAILS.type;
  if (!cameFromEditor) {
    return;
  }
  const tdoId = previousRoute?.payload?.tdoId as TDOId | undefined;
  if (!tdoId) {
    return;
  }
  yield* put(actionRefreshFileOutputs({ tdoIds: [tdoId] }));
}

function* watchRenameFolderOrCase() {
  yield* all([
    takeLatest(RENAME_FOLDER_REQUEST, renameFolderRequest),
    takeLatest(RENAME_FOLDER_SUCCESS, renameFolderSuccess),
    takeLatest(RENAME_FOLDER_FAILURE, renameFolderFailure),
  ]);
}

function* watchMoveFileToCase() {
  yield* all([
    takeLatest(MOVE_FILE_TO_CASE_REQUEST, moveFileToCaseRequest),
    takeLatest(MOVE_FILE_TO_CASE_SUCCESS, moveFileToCaseSuccess),
    takeLatest(MOVE_FILE_TO_CASE_FAILURE, moveFileToCaseFailure),
  ]);
}

function* watchExpandedFolderRequests() {
  yield* all([
    takeLatest(
      FETCH_FOR_EXPANDED_FOLDER_REQUEST,
      fetchForExpandedFolderRequest
    ),
    takeLatest(
      FETCH_FOR_EXPANDED_FOLDER_SUCCESS,
      fetchForExpandedFolderSuccess
    ),
    takeLatest(
      FETCH_FOR_EXPANDED_FOLDER_FAILURE,
      fetchForExpandedFolderFailure
    ),
  ]);
}

/**
 * VE-26730: load a listed file's latest status-history row on demand.
 *
 * The editors fetch this row when they open a file; the folder view opens
 * nothing, so the row a correction note lives on was never in the store here
 * and `useOpenCorrectionNote` always answered "no note" — the confirm-note
 * prompt could not fire from a folder row at all unless the user had opened
 * that same file in an editor earlier in the session.
 *
 * Fetched per row on demand rather than for the whole listing: the SDO read
 * filters on one tdoId at a time (`filter: JSONData` is an exact match, and
 * the `ids` argument takes SDO ids, not TDO ids), so covering a folder would
 * cost one query per file to populate rows nobody is about to change.
 *
 * The read funnel (SECURITY-11) needs this TDO marked read-verified first, and
 * the proof is re-derived HERE rather than trusted from the payload: the file
 * must be in the folder listing the server returned for this user. A dispatch
 * carrying some other id marks nothing and reads nothing.
 */
export function* ensureFileStatusHistory(
  action: ReturnType<typeof ENSURE_FILE_STATUS_HISTORY>
) {
  const { tdoId } = action.payload;
  if (!tdoId || (yield* select(selectStatusHistoryGate)) === 'disabled') {
    return;
  }
  const listed = yield* select(selectSelectedTdoFile(tdoId as TDOId));
  if (!listed?.id) {
    return;
  }
  // Marked before the cache check, and idempotent: a WRITE also populates the
  // latest row (the writer dispatches its own echo), so a file whose status
  // was just changed from a folder row has the row but has never been
  // verified — and the history modal's own page read would be dropped.
  yield* put(actionMarkTdoReadVerified({ tdoId }));
  // Already answered — the row is cached per TDO and only its own writes
  // refresh it. `null` is an answer ("no rows"); only `undefined` is not.
  const known = yield* select((state: StatusHistoryStore) =>
    selectLatestStatusRow(state, tdoId)
  );
  if (known === undefined) {
    yield* put(actionFetchLatestStatusRow({ tdoId }));
  }
}

// VE-30449: the upload dialog's read of its request's file names. takeEvery, and
// takeLatest would not help: the read is a thunk handed off with put, which a
// saga cannot cancel. Each read carries its own requestId instead — it stops at
// its next page once it is no longer the latest, and the reducer ignores a late
// answer from one that was already mid-page.
function* watchRequestRoster() {
  yield* takeEvery(FETCH_REQUEST_ROSTER_REQUEST, fetchRequestRoster);
}

/**
 * VE-32420: per-row columns for the window the list is drawing.
 *
 * `takeLatest`, not `takeEvery`: scrolling produces a window per frame and
 * only the one the user stopped on is worth paying for. The thunk is handed
 * off with `put`, so a saga cannot cancel it — what `takeLatest` prevents is
 * a queue of stale windows being STARTED, which is most of the saving.
 */
function* watchCaseFileRows() {
  yield* takeLatest(FETCH_CASE_FILE_ROWS_REQUEST, fetchCaseFileRows);
}

export function* fetchCaseFileRows(
  action: ReturnType<typeof FETCH_CASE_FILE_ROWS_REQUEST>
) {
  yield* put(Services.fetchCaseFileRows(action.payload));
}

export function* fetchRequestRoster(
  action: ReturnType<typeof FETCH_REQUEST_ROSTER_REQUEST>
) {
  yield* put(Services.fetchRequestRoster(action.payload));
}

function* watchUpdateRedactionStatus() {
  yield* all([
    takeLatest(UPDATE_REDACTION_STATUS_REQUEST, updateRedactionStatusRequest),
    takeLatest(BATCH_APPLY_WATERMARK_REQUEST, batchApplyWatermarkRequest),
    takeEvery(ENSURE_FILE_STATUS_HISTORY, ensureFileStatusHistory),
  ]);
}

function* watchSearchNameExists() {
  yield* all([
    takeEvery(
      SEARCH_NAME_EXISTS_IN_FOLDER_REQUEST,
      searchNameExistsInFolderRequest
    ),
    takeEvery(
      SEARCH_NAME_EXISTS_IN_FOLDER_SUCCESS,
      searchNameExistsInFolderSuccess
    ),
    takeEvery(
      SEARCH_NAME_EXISTS_IN_FOLDER_FAILURE,
      searchNameExistsInFolderFailure
    ),
  ]);
}

function* fetchMoreFilesRequest(
  action: Action<ActionFetchMoreFilesRequest, undefined>
) {
  const actionTypes = [
    FETCH_MORE_FILES_INIT,
    FETCH_MORE_FILES_SUCCESS,
    FETCH_MORE_FILES_FAILURE,
  ] as const;
  const { caseId, limit, offset } = action.payload;
  const variables = { caseId, offset, limit };
  const query = fetchCaseContentsQuery;
  yield* put(
    Services.invokeGqlCall<FetchCaseContentsQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

function* fetchMoreFilesFailure(
  action: Action<[{ message: string }], undefined>
) {
  const message = action?.payload?.[0];
  yield* put(
    enqueueSnackbar({
      message: `Failed to fetch more files:  ${message?.message}`,
      variant: 'error',
    })
  );
}

function* searchCasesFoldersFailHandler({ payload }: Action<string>) {
  yield* put(
    enqueueSnackbar({
      message: `Failed To Search Cases/Folders: ${payload}`,
      variant: 'error',
    })
  );
}

// Worker Sagas
export function* createRootFolder() {
  const actionTypes = [
    CREATE_ROOT_FOLDER_INIT,
    CREATE_ROOT_FOLDER_SUCCESS,
    CREATE_ROOT_FOLDER_FAILURE,
  ] as const;
  const query = createRootFoldersQuery;
  const variables = {};
  yield* put(
    Services.invokeGqlCall<CreateRootFoldersQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

export function* fetchRootFolders(
  action: Action<ActionFetchFolderRequest, undefined>
) {
  yield* syncRootFolderSmartSelection();
  yield* put(
    Services.fetchRootFolders(action.payload, [
      FETCH_ROOT_FOLDERS_INIT,
      FETCH_ROOT_FOLDERS_SUCCESS,
      FETCH_ROOT_FOLDERS_FAILURE,
    ])
  );
}

export function* fetchFolderPermissions(action: Action<FolderId[], undefined>) {
  yield* put(Services.fetchFolderPermissions(action.payload));
}

// The reducer picks which of the returned roots to use but can't read the
// app-config slice, so mirror the current flag value into casemedia state
// before a `rootFolders` response can land.
function* syncRootFolderSmartSelection() {
  const smartSelection = yield* select(selectRootFolderSmartSelection);
  yield* put(actionSetRootFolderSmartSelection(smartSelection));
}

export function* fetchRootFoldersFailure(
  // action: Action<
  //   [{ message: string; name: string }],
  //   { variables: ActionFetchFolderRequest }
  // >
  action: ReturnType<typeof FETCH_ROOT_FOLDERS_FAILURE>
) {
  const { name, message } = action.payload?.[0] || {};
  const intl = sagaIntl();
  const newMessage = `${intl.formatMessage({
    id: 'fetchRootFolderFailed',
    defaultMessage: 'Unable to fetch folders.',
  })} ${
    name === 'not_allowed'
      ? intl.formatMessage({
          id: 'noRedactPermission',
          defaultMessage: 'Please make sure user has REDACT editor role.',
        })
      : message
  }`;

  yield* put(
    enqueueSnackbar({
      message: newMessage,
      variant: 'error',
    })
  );
}

export function* fetchRootFoldersSuccess(
  action: ReturnType<typeof FETCH_ROOT_FOLDERS_SUCCESS>
) {
  const { rootFolders } = action.payload;
  // Only the org-level root (ownerId === null) counts. If the org has root folders
  // but none is the org root — e.g. only the root user's owner-scoped personal folder
  // exists — we must still create the org root, otherwise the root user's uploads are
  // orphaned once other users join the org. See VE-25835.
  const hasRootAdminFolder = rootFolders?.some((folder) =>
    isEmpty(folder.ownerId)
  );
  if (!hasRootAdminFolder) {
    yield* put(actionCreateRootFolder());
  }
}

export function* createRootFolderSuccess(
  action: Action<{ createRootFolders: FolderStateType[] }, undefined>
) {
  const { createRootFolders } = action.payload;
  if (createRootFolders?.length > 0) {
    // VE-26833: reconcile rather than reset — a plain { limit: 30, offset: 0 }
    // full-replaces the list and drops every page past the first.
    yield* refetchAllLoadedRootFolders();
  }
}

export function* createRootFolderFailure(
  action: Action<[{ message: string; name: string }], undefined>
) {
  const intl = sagaIntl();
  const { name, message } = action.payload?.[0] || {};
  const newMessage = `${intl.formatMessage({
    id: 'createRootFolderFailed',
    defaultMessage: 'Failed to create org root folder.',
  })} ${
    name === 'not_allowed'
      ? intl.formatMessage({
          id: 'noRedactPermission',
          defaultMessage: 'Please make sure user has REDACT editor role.',
        })
      : message
  }`;
  yield* put(
    enqueueSnackbar({
      message: newMessage,
      variant: 'error',
    })
  );
}

/**
 * VE-32420: decide whether this request needs a roster read, and start it.
 *
 * THE FIRST PAGE IS THE MEASUREMENT. It is fetched with the full per-row
 * selection, so for a request smaller than a page it is also the entire
 * answer — every file, fully loaded, in one query. Most requests are that
 * size, so most pay once. A FULL page means there are more, and only then
 * does the roster walk start.
 *
 * Ordering matters here and it is not parallel. Which rows are visible
 * depends on the sort, which depends on the roster, so firing a page at
 * offset 0 beside the roster would be guessing which 30 rows matter and
 * usually guessing wrong.
 *
 * A request already KNOWN to be large skips the measurement and goes straight
 * to the roster — that is what `largeRequestCache` is for, and why a large
 * request pays the unusable first page only once.
 *
 * Requests only. A plain folder is not sorted or selected over this way, and
 * Unassigned Media pages SDOs through the legacy-media api.
 */
export function* fetchRosterForSelectedFolder(
  action: ReturnType<typeof actionSetSelectedFolderSuccess>
) {
  const { folderId, isCase, reuseFreshContents } = action.payload;
  if (!isCase || !folderId || isUnassignedFolderId(folderId)) {
    return;
  }
  // A trip that explicitly reuses the contents it left — an editor round trip
  // — should not re-walk a large request for an answer already held.
  const roster = yield* select(selectRequestRoster(folderId as FolderId));
  if (reuseFreshContents && roster?.status === 'ready') {
    return;
  }
  if (isKnownLargeRequest(folderId)) {
    yield* put(actionFetchRequestRoster(folderId as CaseId));
  }
  // Otherwise wait: `rosterFromFirstPage` below reads the page and either
  // builds the roster from it or starts the walk.
}

/**
 * The verdict on that first page.
 *
 * SHORT PAGE: the request is smaller than a page, so the rows just loaded ARE
 * the request. The roster is published straight from them — same entries the
 * walk would have produced — and no second query happens. Everything
 * downstream reads the roster, so it has to exist even when nothing had to be
 * walked to build it.
 *
 * FULL PAGE: there is more than this, so the walk starts and the request is
 * remembered as large, to skip this measurement next time.
 */
export function* rosterFromFirstPage(
  action: ReturnType<typeof FETCH_SUB_FOLDERS_BYID_SUCCESS>
) {
  const variables = action.meta?.variables;
  const folderId = variables?.caseId ?? variables?.folderId;
  // Page one only: a later page says nothing about the request's size.
  if (!variables?.isCase || !folderId || variables.offset) {
    return;
  }
  if (isUnassignedFolderId(folderId) || isKnownLargeRequest(folderId)) {
    return;
  }
  // ANSWERS THE REQUEST STILL OPEN, OR NOT AT ALL. `fetchSubFolders` dispatches
  // a thunk, so this success escapes the `takeLatest` above it the same way the
  // window fetch's answer did. Both branches below REPLACE the roster map, so a
  // page one landing late from the request the user just left would wipe the
  // roster of the one now open — dropping it back to pre-roster behaviour, with
  // Select All disabled, until it is re-selected.
  const openFolders = yield* select(selectSelectedFolders);
  if (openFolders?.[openFolders.length - 1]?.id !== folderId) {
    return;
  }
  // The success payload is typed `any` (a pre-existing TODO on that action),
  // so the shape is narrowed here rather than trusted.
  const page = action.payload as
    | { folder?: { childTDOs?: { records?: unknown[] | null } | null } }
    | undefined;
  const records: unknown[] = page?.folder?.childTDOs?.records ?? [];
  // `?? Infinity`, not `?? 0`: a missing limit is a wiring fault, not a large
  // request. Defaulting to 0 made EVERY page look full, so every request would
  // be cached as large and start a roster walk.
  const isFullPage = records.length >= (variables.limit ?? Infinity);
  if (isFullPage) {
    rememberLargeRequest(folderId, records.length);
    yield* put(actionFetchRequestRoster(folderId as CaseId));
    return;
  }
  // Short page. Build the roster from what just arrived — one query served
  // both the rows and the index.
  yield* put(
    actionPublishRequestRoster(
      folderId as CaseId,
      rosterEntriesFrom(records as Parameters<typeof rosterEntriesFrom>[0])
    )
  );
}

export function* setSelectedFolder(
  action: Action<ActionSetSelectedFolderPayload, undefined>
) {
  const defaultLimit = yield* select(selectDefaultLimit);
  const defaultOffset = yield* select(selectDefaultOffset);
  const { isCase, folder } = action.payload;
  const selectedId = folder?.id;

  // VE-26943: Unassigned Media has no aiWARE folder to fetch — its contents come
  // from the redact-state SDO list, and the reducer has already pointed the
  // virtual input folder at it. Dispatching success here would send the sentinel
  // id to fetchCaseContentsQuery. Just make sure page one is loaded (a user can
  // reach the folder before the mount probe has answered).
  if (isUnassignedFolderId(selectedId)) {
    const alreadyLoaded = yield* select(selectLegacyMedias);
    if (!(alreadyLoaded?.length > 0)) {
      yield* put(
        actionFetchAllLegacyMedias({
          limit: defaultLimit,
          offset: defaultOffset,
        })
      );
    }
    return;
  }

  if (selectedId) {
    yield* put(
      actionSetSelectedFolderSuccess({
        folderId: selectedId,
        limit: defaultLimit,
        offset: defaultOffset,
        isCase: isCase,
        // VE-28429: relayed, never decided here. This saga serves every
        // dispatcher of SET_SELECTED_FOLDER_REQUEST — the tree, search, the
        // Refresh button, the post-ingestion refresh — so deciding here would
        // hand all of them a cache they never asked for.
        reuseFreshContents: action.payload?.reuseFreshContents,
      })
    );
  }
}

/**
 * VE-28429: can this request's file list be put straight back instead of
 * refetched?
 *
 * Opening and closing a file re-selects the folder it lives in, and on a large
 * request `fetchCaseContentsQuery` is slow — so browsing a few files in a row
 * paid for a full reload each time, for a list that almost certainly had not
 * changed.
 *
 * VE-30022 re-based the window. It used to ask how old the LIST was, which is
 * not the question: a request read for two minutes before a document was opened
 * was already outside it, so a ten-second trip into that document still cost a
 * full reload, and only the first exit of a browsing run was ever free. It now
 * asks how long we have been AWAY (`canReuseAfterTrip` — see ../contants), so
 * the cost of a trip is a function of the trip. `caseContentsLoadedAt` still
 * records when the list landed; nothing here reads it any more.
 *
 * Every condition here is a way the skip could show the user something wrong,
 * so all four must hold:
 *
 * - **Case contents only.** The folder listing is a different query, with its
 *   own counterpart below.
 * - **Page one only.** Defensive: paging runs through its own FETCH_MORE_FILES_*
 *   family and does not reach this saga today, and every dispatcher passes offset
 *   0 — but a caller that did reuse this path at an offset would have its page
 *   silently dropped, which is not a failure mode worth leaving open.
 * - **A short trip, to one of our own columns.** Both halves matter: the time
 *   bounds how stale the list may be, and the column list stops a folder the
 *   trip never showed being served from a session-long row cache.
 * - **We must still hold THIS request's rows.** The trip outlives the data —
 *   select another request and back and `caseFiles` is gone, so skipping would
 *   leave an empty list; and rows held for a request we are not showing say
 *   nothing about the one we are. So the virtual folder's parent must be this
 *   folder, with `caseFiles` present — present, not non-empty: a request with
 *   no files is loaded, just empty.
 *
 * Anything that edits the list — move, trash, upload, status change, a finished
 * redaction — refreshes through its own action rather than this path, so a skip
 * cannot hide the user's own changes.
 */
export function* isCaseContentsFresh(folderId: FolderId, offset: number) {
  const trip = yield* select(selectFolderViewTrip);
  const virtualFolder = yield* select(selectSelectedVirtualFolder);
  return (
    !offset &&
    canReuseAfterTrip(trip, folderId) &&
    virtualFolder?.parent?.id === folderId &&
    !!virtualFolder.parent.caseFiles
  );
}

/**
 * The folder-listing counterpart of `isCaseContentsFresh`, for the column one
 * level out from a request. Same rules — page one, a short trip that showed
 * this folder, and the listing actually on the folder's chain entry (the
 * reducer puts it back on re-select) — minus the virtual-folder test, which
 * only requests have.
 */
export function* isSubFolderListingFresh(folderId: FolderId, offset: number) {
  const trip = yield* select(selectFolderViewTrip);
  const selectedFolders = yield* select(selectSelectedFolders);
  return (
    !offset &&
    canReuseAfterTrip(trip, folderId) &&
    !!selectedFolders?.find((f) => f?.id === folderId)?.subfolders
  );
}

export function* fetchSubFolders(
  action: Action<ActionFetchSubFoldersPayload, undefined>
) {
  const actionTypes = [
    FETCH_SUB_FOLDERS_BYID_REQUEST,
    FETCH_SUB_FOLDERS_BYID_SUCCESS,
    FETCH_SUB_FOLDERS_BYID_FAILURE,
  ] as const;
  const { isCase, folderId, limit, offset, reuseFreshContents } =
    action.payload;
  const fresh = !reuseFreshContents
    ? false
    : isCase
      ? yield* call(isCaseContentsFresh, folderId, offset)
      : yield* call(isSubFolderListingFresh, folderId, offset);
  if (fresh) {
    return;
  }
  const variables = isCase
    ? { caseId: folderId, offset, limit, isCase }
    : { folderId: folderId, offset, limit };
  const query = isCase ? fetchCaseContentsQuery : fetchSubFoldersQuery;
  yield* put(
    Services.invokeGqlCall<
      FetchCaseContentsQueryResponse | FetchSubFoldersQueryResponse
    >(query, variables, actionTypes, { returnPartialDataOnNotFound: true })
  );
}

function* fetchSubFoldersFailure(
  action: ReturnType<typeof FETCH_SUB_FOLDERS_BYID_FAILURE>
) {
  const { message } = action.payload?.[0] || {};
  yield* put(
    enqueueSnackbar({
      message: `Failed to fetch folders: ${message}`,
      variant: 'error',
    })
  );
}

function* createNewFolder(action: Action<CreateNewFolderPayload, undefined>) {
  const { newFolderName, parentId, isCase, isRoot, parentIsCase } =
    action.payload;
  yield* put(
    Services.createNewFolder({
      newFolderName,
      parentId,
      isCase,
      isRoot,
      parentIsCase,
    })
  );
}

export function* createNewFolderSuccess(
  // Typed off the action creator (not CreateNewFolderPayload) so the fields
  // destructured here are exactly the ones the service actually dispatches —
  // reading a phantom field can't type-check again.
  action: ReturnType<typeof CREATE_NEW_FOLDER_SUCCESS>
) {
  const intl = sagaIntl();
  const { newFolderName, parentId, isRoot, parentIsCase } = action.payload;
  const offset = yield* select(selectDefaultOffset);
  const limit = yield* select(selectDefaultLimit);
  if (isRoot) {
    // VE-26833: see refetchAllLoadedRootFolders — creating a root folder must
    // not collapse the list back to page 1.
    yield* refetchAllLoadedRootFolders();
  } else {
    // Refresh the parent's children in each cache that actually holds them
    // (the fetch responses are dropped by their reducers otherwise).
    // parentIsCase comes from the Add dialog — which held the parent object
    // — so the refetch query (caseContents vs subfolders) is right even
    // when the parent has left both caches (e.g. the user navigated away
    // during the service's DATABASE_UPDATE_DELAY).
    const selectedFolders = yield* select(selectSelectedFolders);
    if (selectedFolders?.some((folder) => folder.id === parentId)) {
      yield* put(
        actionSetSelectedFolderSuccess({
          folderId: parentId,
          limit,
          offset,
          isCase: parentIsCase,
        })
      );
    }

    const expandedFolders = yield* select(selectAllExpandedFolders);
    if (expandedFolders?.some((folder) => folder.id === parentId)) {
      yield* put(
        actionFetchForExpandedFolders({ folderId: parentId, limit, offset })
      );
    }
  }

  yield* put(
    enqueueSnackbar({
      message: `${newFolderName} ${intl.formatMessage({
        id: 'createdSuccessfully',
        defaultMessage: 'created successfully',
      })}`,
      variant: 'success',
    })
  );
}

function* createNewFolderFailure(
  action: Action<{ newFolderName: string }, undefined>
) {
  const intl = sagaIntl();
  const { newFolderName } = action.payload;

  yield* put(
    enqueueSnackbar({
      message: `${newFolderName} ${intl.formatMessage({
        id: 'failedToCreate',
        defaultMessage: 'could not be created',
      })}`,
      variant: 'error',
    })
  );
}

// Upper bound on how long deleteCaseSdoRequest waits for the folder-delete
// outcome before giving up on purging the SDO (see the race below). Generous so
// a legitimately slow delete still reports DELETE_FOLDER_SUCCESS first; it only
// bounds the silent-guard cases that emit no terminal action.
const DELETE_FOLDER_OUTCOME_TIMEOUT_MS = 60_000;

// Backstop for a status-update terminal action that never arrives (see
// performUpdateRedactionStatus). Same rationale as the folder-delete timeout.
const UPDATE_STATUS_OUTCOME_TIMEOUT_MS = 60_000;

export function* deleteCaseSdoRequest(
  action: Action<ActionDeleteCaseSdoPayload, undefined>
) {
  const { treeObjectId, folderId, folderName } = action.payload;
  // Fire the lock-guarded delete. deleteCaseSdo now removes the FOLDER (and its
  // content-template link) first; the SDO is purged below only once that
  // succeeds, so an interrupted delete can't strand a dangling reference
  // (VE-26016 — mirrors the trash-side ordering fix).
  yield* put(
    Services.callIfFolderCaseIsUnlocked({
      treeObjectId,
      isCase: true,
      treeObjectName: folderName,
      call: () => Services.deleteCaseSdo(action.payload),
    })
  );

  // Await the folder-delete outcome. Match on the folder id so a concurrent,
  // unrelated folder delete can't be mistaken for this one. TDO_IN_USE covers
  // the locked case (the guard then fires neither success nor failure). Only a
  // genuine DELETE_FOLDER_SUCCESS proceeds to purge the now-orphaned SDO; every
  // other outcome leaves the SDO in place, so the case stays intact and
  // retryable rather than dangling.
  //
  // The `delay` is a safety backstop: callIfFolderCaseIsUnlocked can also return
  // WITHOUT emitting any terminal action (its `!schemaId` early return, or the
  // non-empty-folder block that only shows a snackbar). Without it the saga would
  // wait forever. On timeout we skip the purge — the folder wasn't deleted, so
  // the SDO must stay; at worst a slow-but-successful delete leaves a harmless
  // orphaned SDO, never a dangling link.
  const { outcome } = yield* race({
    outcome: take(
      (candidate: { type: string; meta?: { variables?: { id?: string } } }) =>
        candidate.type === TDO_IN_USE.type ||
        ((candidate.type === DELETE_FOLDER_SUCCESS.type ||
          candidate.type === DELETE_FOLDER_FAILURE.type) &&
          (candidate.meta?.variables?.id === folderId ||
            candidate.meta?.variables?.id === treeObjectId))
    ),
    timeout: delay(DELETE_FOLDER_OUTCOME_TIMEOUT_MS),
  });

  if (outcome?.type === DELETE_FOLDER_SUCCESS.type) {
    yield* put(Services.purgeCaseSdo(action.payload));
  }
}

function* softDeleteCaseSdoRequest(
  action: Action<ActionSoftDeleteCaseSdoPayload, undefined>
) {
  const { treeObjectId, data } = action.payload;
  const trashRetentionDays = yield* select(selectTrashRetentionDays);
  const user = yield* select(selectUser);
  const trashFolderId = yield* call(ensureTrashFolderId);

  const payload = {
    ...action.payload,
    data: {
      ...data,
      oldFolderPath: data.oldFolderPath
        ? sortFolderPath(data.oldFolderPath)
        : undefined,
      trashRetentionDays,
      movedToTrashDateTime: moment().toISOString(),
      movedToTrashByName: user?.userName || user?.email || '',
      movedToTrashById: user?.userId || '',
      isCase: true,
    },
  };

  if (!trashFolderId) {
    yield* put(
      enqueueSnackbar({
        message: `Failed to move ${data.caseName} to trash. Please try again.`,
        variant: 'error',
      })
    );
    return;
  }

  yield* put(
    Services.callIfFolderCaseIsUnlocked({
      treeObjectId,
      isCase: true,
      treeObjectName: data.caseName,
      intent: 'trash',
      call: () => Services.softDeleteCaseSdo(payload, trashFolderId),
    })
  );
}

function* deleteCaseSdoFailure(
  action: Action<ActionDeleteCaseSdoPayload, undefined>
) {
  const { folderName, suppressSnackbar } = action.payload;
  if (suppressSnackbar) {
    return;
  }
  yield* put(
    enqueueSnackbar({
      message: `${folderName} delete failed.`,
      variant: 'error',
    })
  );
}

function* deleteFolderRequest(
  action: Action<{
    treeObjectId: TreeObjectId;
    folderId?: FolderId;
    folderName: string;
    isPermanentDelete?: boolean;
    suppressSnackbar?: boolean;
    isCase?: boolean;
  }>
) {
  const actionTypes = [
    DELETE_FOLDER_INIT,
    DELETE_FOLDER_SUCCESS,
    DELETE_FOLDER_FAILURE,
  ] as const;
  const {
    treeObjectId,
    folderId,
    folderName,
    isPermanentDelete,
    suppressSnackbar,
    isCase,
  } = action.payload;
  // VE-21114: v2 authorizes deleteFolder via OLP against the folder id; the
  // deprecated treeObjectId returns not_allowed. Reads still accept either, so
  // just swap the identifier under v2 (v1 keeps treeObjectId).
  const isV2Folders = yield* select(selectV2FoldersEnabled);
  const variables = {
    id: resolveFolderMutationId(isV2Folders, {
      folderId,
      treeObjectId,
    }) as unknown as TreeObjectId,
    folderName,
    isPermanentDelete,
    suppressSnackbar,
  };
  const query = deleteFolderOrCaseQuery;
  const deleteCall = isCase
    ? () =>
        Services.deleteFolderCaseAndFiles<DeleteFolderOrCaseQueryResponse>(
          query,
          variables,
          actionTypes
        )
    : () =>
        Services.deleteFolderAndSubfolders<DeleteFolderOrCaseQueryResponse>(
          query,
          variables,
          actionTypes
        );
  yield* put(
    Services.callIfFolderCaseIsUnlocked({
      treeObjectId,
      isCase: !!isCase,
      treeObjectName: folderName,
      call: deleteCall,
    })
  );
}

function* softDeleteFolderRequest(
  action: Action<{
    folderId: FolderId;
    folderName: string;
    treeObjectId: TreeObjectId;
    oldFolderPath: FolderPathItem[];
  }>
) {
  const { folderId, folderName, treeObjectId, oldFolderPath } = action.payload;
  const trashRetentionDays = yield* select(selectTrashRetentionDays);
  const user = yield* select(selectUser);

  const trashFolderId = yield* call(ensureTrashFolderId);

  if (!trashFolderId) {
    yield* put(
      enqueueSnackbar({
        message: `Failed to move ${folderName} to trash. Please try again.`,
        variant: 'error',
      })
    );
    return;
  }

  const payload = {
    folderId,
    folderName,
    treeObjectId,
    trashRetentionDays,
    oldFolderPath: sortFolderPath(oldFolderPath),
    movedToTrashByName: user?.userName || user?.email || '',
    movedToTrashById: user?.userId || '',
  };

  yield* put(
    Services.callIfFolderCaseIsUnlocked({
      treeObjectId,
      isCase: false,
      // A folder, not a request — the guard names whichever nested request
      // holds the open file instead.
      intent: 'trash',
      call: () => Services.softDeleteFolder(payload, trashFolderId),
    })
  );
}

// VE-26833: reconcile the root list against the server after a folder joins or
// leaves it, re-requesting every page the user has loaded as ONE page at
// offset 0 — mapRootFolder full-replaces at offset 0 only, and that is what
// makes the response authoritative rather than additive. The reconcile
// therefore depends on `defaultFetchMoreOffset` being exactly 0: it is, and
// nothing in src/ ever writes it, but a non-zero default would silently turn
// this into an appending fetch.
//
// The page count comes from `fetchSubFolderOffset`, which names the LAST PAGE
// on screen, not a count: with 60 folders loaded it is 30. So the loaded total
// is that offset plus one page. The previous formula used the offset itself as
// the limit (`max(currentOffset, defaultOffset)`), which asked for 30 when 60
// were on screen and truncated the list by exactly one page at every depth —
// and sent `limit: 0` whenever the user had never hit Load more, since the
// query passes $limit explicitly and a GraphQL schema default only fills in for
// an ABSENT argument.
function* refetchAllLoadedRootFolders() {
  const defaultOffset: number = yield* select(selectDefaultOffset);
  const defaultLimit: number = yield* select(selectDefaultLimit);
  const currentOffset: number = yield* select(selectRootFoldersOffset);
  yield* put(
    actionFetchFolderRequest({
      limit: currentOffset + defaultLimit,
      offset: defaultOffset,
    })
  );
}

function* deleteFolderSuccess(
  action: Action<
    unknown,
    { variables: { folderName: string; suppressSnackbar?: boolean } }
  >
) {
  const { folderName, suppressSnackbar } = action.meta.variables;
  yield* refetchAllLoadedRootFolders();
  if (suppressSnackbar) {
    return;
  }
  yield* put(
    enqueueSnackbar({
      message: `${folderName} deleted successfully.`,
      variant: 'success',
    })
  );
}

// Upper bound on how long softDeleteFolderSuccess waits for the trash-move
// outcome before refetching root folders anyway (VE-26484). Generous so a slow
// move still resolves the wait itself; refetching before the move commits
// resurrects the folder in the tree, so err on the side of waiting.
const TRASH_MOVE_OUTCOME_TIMEOUT_MS = 60_000;

export function* softDeleteFolderSuccess(action: {
  payload: {
    id: TreeObjectId;
    folderName: string;
  };
}) {
  const { id, folderName } = action.payload;
  yield* put(
    enqueueSnackbar({
      message: `${folderName} was moved to trash successfully.`,
      variant: 'success',
    })
  );
  // VE-26484: SOFT_DELETE_FOLDER_SUCCESS is dispatched while the relocation
  // into Trash (MOVE_FOLDER_REQUEST, fired by Services.softDeleteFolder) is
  // still in flight. Refetching root folders immediately raced that move: the
  // server would still return the folder under its old parent — now wearing
  // the trash-SDO contentTemplate link, i.e. a briefcase icon — resurrecting
  // it until a manual refresh. The reducer already prunes it from local state,
  // so wait for this folder's move outcome before the reconciling refetch.
  // Match on treeObjectId so a concurrent unrelated move can't satisfy the
  // wait; the delay is a backstop so the refetch always eventually happens
  // (same pattern as deleteCaseSdoRequest above). Refetch on failure too — the
  // tree must converge to server truth, not stay optimistically pruned.
  yield* race({
    outcome: take(
      (candidate: {
        type: string;
        meta?: { variables?: { treeObjectId?: string } };
      }) =>
        (candidate.type === MOVE_FOLDER_SUCCESS.type ||
          candidate.type === MOVE_FOLDER_FAILURE.type) &&
        candidate.meta?.variables?.treeObjectId === id
    ),
    timeout: delay(TRASH_MOVE_OUTCOME_TIMEOUT_MS),
  });
  // The move has settled (or the backstop fired), so the folder is no longer at
  // risk of coming back from the server under its old parent — stop filtering it
  // out of refetches and let the reconciling fetch below decide the truth.
  // Released on the timeout path too, so a move whose outcome is never observed
  // can't hide the folder forever.
  yield* put(actionReleasePendingTrashFolders([id]));
  // Read the offsets only now: the wait above can last up to 60s, during which
  // the user may have loaded more root folders. A stale limit at offset 0
  // full-replaces the list (mapRootFolder) and would drop those pages.
  yield* refetchAllLoadedRootFolders();
}

function* deleteFolderFailure(
  action: Action<
    ReadonlyArray<{ message?: string }> | undefined,
    { variables: { folderName: string; suppressSnackbar?: boolean } }
  >
) {
  const { folderName, suppressSnackbar } = action.meta.variables;
  if (suppressSnackbar) {
    return;
  }
  const intl = sagaIntl();
  // VE-21114: surface the real API error (e.g. v2's "folder not empty") instead
  // of the generic message. The payload is a GraphQL errors array on API-level
  // failures, but callGraphQLApi dispatches the raw Error on transport/exception
  // failures — guard with Array.isArray so `.find` never runs on a non-array.
  const isErrorsArray = (
    p: unknown
  ): p is ReadonlyArray<{ message?: string }> => Array.isArray(p);
  const apiMessage = isErrorsArray(action.payload)
    ? action.payload.find((e) => e?.message)?.message?.trim()
    : undefined;
  yield* put(
    enqueueSnackbar({
      message: apiMessage
        ? intl.formatMessage(
            {
              id: 'deleteFolderFailureWithReason',
              defaultMessage: '{folderName} could not be deleted: {reason}',
            },
            { folderName, reason: apiMessage }
          )
        : intl.formatMessage(
            {
              id: 'deleteFolderFailure',
              defaultMessage: '{folderName} delete failed.',
            },
            { folderName }
          ),
      variant: 'error',
    })
  );
}

function* softDeleteFolderFailure(action: {
  payload: {
    id: TreeObjectId;
    folderName: string;
  };
}) {
  const { folderName } = action.payload;
  yield* put(
    enqueueSnackbar({
      message: `${folderName} delete failed.`,
      variant: 'error',
    })
  );
}

function* deleteFileRequest(
  action: Action<ActionDeleteFilePayload, undefined>
) {
  yield* performDeleteFile(action.payload);
}

// Core single-file delete used both by the DELETE_FILE_REQUEST handler above
// and by the batch-trash saga (VE-25098). Awaits the delete result (and the
// trash relocation for soft deletes) and returns whether the file was
// successfully removed, so the batch caller can sequence files and report one
// summary snackbar. The per-file success/failure snackbars are suppressed when
// `payload.suppressSnackbar` is set (see deleteFileSuccess/deleteFileFailure).
export function* performDeleteFile(
  payload: ActionDeleteFilePayload,
  // When present, use this pre-resolved file instead of re-reading it from
  // folder-view state. The batch-trash saga snapshots each file up front so a
  // navigation (which clears the folder state) can't strand the remaining
  // deletes — the saga keeps running in the background with the snapshot.
  preFetched?: {
    file: AssetFileType | LegacyMediaDetailType | undefined;
    // VE-32420: the open request's folder chain, snapshotted by the batch for
    // the same reason the file is — a navigation mid-batch clears the columns
    // this would otherwise be rebuilt from.
    requestFolderPath?: FolderPathItem[];
  }
) {
  const actionTypes = [
    DELETE_FILE_INIT,
    DELETE_FILE_SUCCESS,
    DELETE_FILE_FAILURE,
  ] as const;
  const { tdoId, type, isSoftDelete, isLegacyTdo } = payload;
  const currentTime = moment().toISOString();
  const trashRetentionDays = yield* select(selectTrashRetentionDays);
  let selectedFile: AssetFileType | LegacyMediaDetailType | undefined;
  let oldFolderPath: FolderPathItem[] = [];
  // Fallback source folder for the move-to-trash relocation when the resolved
  // file carries no folder membership (see the caseMedia branch below). VE-25125.
  let caseFolderId: string | undefined;
  let variables = { ...payload };
  // Resolve the file for BOTH delete kinds. This used to sit inside the
  // `isSoftDelete` block below, because only the trash path needed its tags and
  // folder membership — but the lock guard also names it in a refusal, and a
  // permanent delete left that undefined, so the one path whose verb really is
  // "Cannot delete" was the one that could not say which file was open.
  // Nothing here is soft-delete-specific; the tag rewrite that is stays below.
  if (preFetched) {
    selectedFile = preFetched.file;
  } else if (isLegacyTdo) {
    const medias = yield* select(selectLegacyMedias);
    selectedFile = (medias || []).find((m) => m.tdoId === tdoId);
  } else {
    selectedFile = yield* select(selectSelectedTdoFile(tdoId));
    if (!selectedFile) {
      const caseMedia = yield* select(selectCaseMedia);
      const mediaEntry = caseMedia?.media?.find((m) => m.id === tdoId);
      if (mediaEntry) {
        selectedFile = mediaEntry as unknown as AssetFileType;
        // Case-media files (the editor's media-list drawer) are direct children
        // of the case folder, but fetchCaseMedia doesn't return their folder
        // membership — so remember the case folder as the source for the
        // move-to-trash relocation below. Without it oldFolderId is empty and
        // the soft delete only tags the file, leaving it orphaned: hidden from
        // the case yet never filed into Trash. VE-25125.
        caseFolderId = caseMedia?.caseId;
      }
    }
  }
  if (isSoftDelete) {
    const updatedTags = [...(selectedFile?.details?.tags || [])];
    // VE-21114: under v2 the TDO's folderPath excludes its own (leaf) folder, so
    // re-attach it via tdoFolderChain. This keeps both the stamped Original
    // Location AND the relocate below (oldFolderPath.at(-1) = the file's real
    // folder) correct under v2 — otherwise moveTdo gets the parent folder as
    // oldFolderId and fails ("failed to relocate file"). No-op under v1.
    const isV2Folders = yield* select(selectV2FoldersEnabled);
    const leafFolder =
      selectedFile && 'folders' in selectedFile
        ? selectedFile.folders?.[0]
        : undefined;
    oldFolderPath = tdoFolderChain(leafFolder, isV2Folders);
    // VE-32420: a row rebuilt from the roster has no `folders` — the roster
    // drops that join because it is a per-record walk recomputing a path that
    // belongs to the request. Without a path the tag carries no Original
    // Location AND `oldFolderId` below stays empty, which picks the
    // file-into-Trash mutation over the move: the file ends up filed under
    // BOTH the request and Trash, hidden by its own tag, with nothing for
    // Restore to restore to. The file is in the open request, whose chain the
    // breadcrumb already holds.
    //
    // GATED ON BEING A ROSTER ROW, not merely on the path being empty. An
    // UNASSIGNED file legitimately has no folders — being parentless is what
    // makes it unassigned — and VE-26943 depends on that empty path: it stamps
    // `wasUnassignedMedia` instead, and the empty `oldFolderId` is what selects
    // the file-into-Trash mutation, which is the right verb for a file in no
    // folder. Substituting the open column's chain there hands `moveTdo` the
    // unassigned SENTINEL, which is not an aiWARE folder id, so the relocate
    // fails and the file is tagged deleted but never filed into Trash.
    if (
      !oldFolderPath.length &&
      !isLegacyTdo &&
      !isUnassignedFile(selectedFile) &&
      isRosterOnlyRow(selectedFile)
    ) {
      const requestChain =
        preFetched?.requestFolderPath ??
        folderChainToPath(
          yield* select(selectSelectedFolders),
          yield* select(selectRootFolderId)
        );
      // Root + request at minimum. A shorter chain is one that could not be
      // anchored, and an unanchored path restores worse than no path at all.
      if (requestChain.length >= 2) {
        oldFolderPath = requestChain;
      }
    }

    // Record who moved the file to trash, alongside the timestamp.
    const user = yield* select(selectUser);
    const movedToTrashByName = user?.userName || user?.email || '';
    const movedToTrashById = user?.userId || '';

    if (updatedTags?.[0]) {
      updatedTags[0] = {
        ...updatedTags[0],
        trashRetentionDays: String(trashRetentionDays),
        movedToTrashDateTime: currentTime,
        movedToTrashByName,
        movedToTrashById,
        ...(oldFolderPath.length ? { oldFolderPath } : {}),
        // VE-26943: an unassigned file has no folder path to stamp, which the
        // Trash table could not tell apart from a genuinely missing one (it
        // showed '--' for both). Mark it explicitly so Trash can name where it
        // came from.
        ...(isLegacyTdo ? { wasUnassignedMedia: 'true' } : {}),
      };
    } else {
      updatedTags.push({
        value: IN_REDACTION_TAG_KEY,
        toBeDeletedTime: moment(new Date()).toISOString(),
        movedToTrashByName,
        movedToTrashById,
      });
    }
    if (type === VirtualFolderNames.INPUT && selectedFile) {
      variables = {
        ...{
          ...variables,
          details: {
            ...(selectedFile?.details || {}),
            tags: updatedTags,
          },
        },
      };
    }
  }
  const outputFiles = yield* select(selectSelectedOutputFiles);
  const query =
    type === VirtualFolderNames.INPUT
      ? isSoftDelete
        ? softDeleteFileQuery
        : deleteFileQuery
      : deleteOutputFileQuery(
          outputFiles
            // VE-26943: was `f.id.startsWith(tdoId)` — see assetBelongsToFile.
            .filter((f) => assetBelongsToFile(f, tdoId))
            .map((f) => f.id)
        );
  yield* put(
    Services.callIfTdoIsUnlocked({
      tdoId,
      fileName: lockRefusalFileName(selectedFile, tdoId),
      intent: isSoftDelete ? 'trash' : 'delete',
      call: () =>
        Services.invokeGqlCall<
          | SoftDeleteFileQueryResponse
          | DeleteFileQueryResponse
          | DeleteOutputFileQueryResponse
        >(query, variables, actionTypes),
    })
  );

  // Await the delete result, correlated on the terminal action's own tdoId —
  // redux-saga's channel is multicast, so a type-only match lets one file's
  // saga wake on another's outcome. That used to decide a boolean; it now
  // gates purgeTdoSideData, which HARD-deletes this file's status history, so
  // a mismatched wake-up would purge the trail of a file whose delete failed.
  // Same shape as performUpdateRedactionStatus below. TDO_IN_USE is matched on
  // type alone (as it is there): a locked file never fires success/failure, and
  // it is the signal that keeps a batch caller from blocking indefinitely.
  const deleteResult: { type: string } = yield* take(
    (candidate: { type: string; meta?: { variables?: { tdoId?: string } } }) =>
      candidate.type === TDO_IN_USE.type ||
      ((candidate.type === DELETE_FILE_SUCCESS.type ||
        candidate.type === DELETE_FILE_FAILURE.type) &&
        candidate.meta?.variables?.tdoId === tdoId)
  );

  if (deleteResult.type !== DELETE_FILE_SUCCESS.type) {
    return false;
  }

  // For soft-delete ("move to trash") we also physically move the TDO into a
  // dedicated system folder so other apps don't see it under the original path.
  if (isSoftDelete) {
    const trashFolderId = yield* call(ensureTrashFolderId);
    if (!trashFolderId) {
      // The file is already soft-deleted (tagged) — only the relocation into
      // the trash system folder couldn't proceed. Treat as a success.
      yield* put(DELETE_FILE_RELOCATED({ tdoId }));
      return true;
    }

    const oldFolderId =
      oldFolderPath.at(-1)?.id || selectedFile?.parentFolderId || caseFolderId;

    const moveToTrashActionTypes = [
      'MOVE_TO_TRASH_INIT',
      'MOVE_TO_TRASH_SUCCESS',
      'MOVE_TO_TRASH_FAILURE',
    ] as const;

    const moveVariables = {
      tdoId,
      newFolderId: trashFolderId,
      oldFolderId: oldFolderId,
    };
    const moveQuery = isEmpty(oldFolderId)
      ? fileTdoWithCaseQuery
      : moveTdoQuery;

    yield* put(
      Services.callIfTdoIsUnlocked({
        tdoId,
        fileName: lockRefusalFileName(selectedFile, tdoId),
        intent: 'trash',
        call: () =>
          Services.invokeGqlCall<
            FileTdoWithCaseQueryResponse | MoveTdoQueryResponse
          >(moveQuery, moveVariables, moveToTrashActionTypes),
      })
    );

    const moveResult: { type: string } = yield* take([
      moveToTrashActionTypes[1],
      moveToTrashActionTypes[2],
    ]);

    if (moveResult.type === moveToTrashActionTypes[2]) {
      yield* put(
        enqueueSnackbar({
          message: 'Moved to trash, but failed to relocate file.',
          variant: 'warning',
        })
      );
    }

    // Relocation attempt is done — let any caller waiting to tear down (e.g. the
    // editor exit for the file open in the drawer) proceed only now. VE-25125.
    yield* put(DELETE_FILE_RELOCATED({ tdoId }));
  } else if (type === VirtualFolderNames.INPUT) {
    // VE-26730: a hard delete here (trashRetentionDays: 0) used to leave every
    // per-TDO SDO behind — the trash sweeper cleaned up after itself and this
    // path did not. Same saga, so the two cannot drift again.
    yield* call(purgeTdoSideData, [tdoId]);
  }

  // VE-26943: no refetch here. Re-reading from offset 0 REPLACED the whole
  // list with page one, so deleting one file threw away everything the user had
  // scrolled in. The reducer drops the row in place instead (DELETE_FILE_SUCCESS).

  return true;
}

function* deleteFileSuccess(
  action: Action<unknown, { variables: ActionDeleteFilePayload }>
) {
  const { fileName, isLegacyTdo, isSoftDelete, suppressSnackbar } =
    action.meta.variables;
  // The batch-trash saga (VE-25098) shows a single summary snackbar instead.
  if (!isLegacyTdo && !suppressSnackbar) {
    yield* put(
      enqueueSnackbar({
        message: isSoftDelete
          ? `${fileName} was moved to trash successfully.`
          : `${fileName} delete successfully.`,
        variant: 'success',
      })
    );
  }
}

function* deleteFileFailure(
  // action: Action<undefined, { variables: { fileName: string } }>
  action: ReturnType<typeof DELETE_FILE_FAILURE>
) {
  const { fileName, isSoftDelete, suppressSnackbar } = action.meta.variables;
  // The batch-trash saga (VE-25098) reports failures in its summary snackbar.
  if (suppressSnackbar) {
    return;
  }
  yield* put(
    enqueueSnackbar({
      message: isSoftDelete
        ? `${fileName} failed to move to trash.`
        : `${fileName} failed to delete.`,
      variant: 'error',
    })
  );
}

// VE-25098: trash (or permanently delete) a set of multi-selected input files.
// Runs sequentially so each file's DELETE_FILE_SUCCESS/FAILURE (which the
// reducer uses to drop that row) is awaited before the next, then reports a
// single summary snackbar. Per-file snackbars are suppressed via
// `suppressSnackbar`.
function* batchTrashFilesRequest(
  action: Action<ActionBatchTrashFilesPayload, undefined>
) {
  const { files, isSoftDelete } = action.payload;
  const intl = sagaIntl();
  const caseId = yield* select(selectMultiSelectCaseId);
  let succeeded = 0;
  let failed = 0;
  let remaining = files.length;

  // Snapshot each file from folder-view state up front (synchronously, before
  // any await), so a later navigation that clears that state can't strand the
  // remaining deletes — the saga keeps running in the background off the
  // snapshot. See performDeleteFile's `preFetched` param.
  //
  // VE-32420: the request's folder chain goes with them, for the same reason.
  // Rows selected past the first page are roster rows carrying no folder
  // membership of their own, so this is what they are trashed OUT of.
  // Never for Unassigned Media: its chain's leaf is a sentinel, not a folder.
  const requestFolderPath = isUnassignedFolderId(caseId)
    ? []
    : folderChainToPath(
        yield* select(selectSelectedFolders),
        yield* select(selectRootFolderId)
      );
  const fileSnapshots = new Map<string, AssetFileType | undefined>();
  for (const file of files) {
    fileSnapshots.set(
      file.tdoId,
      yield* select(selectSelectedTdoFile(file.tdoId))
    );
  }

  // Mark the selected rows for removal (triggers their exit animation) and drop
  // the selection. Wait out the animation before the actual deletes start so
  // rows are already collapsed when their DELETE_FILE_SUCCESS unmounts them —
  // avoids a mid-animation pop.
  yield* put(actionSetPendingRemovalFiles(files.map((f) => f.tdoId)));
  yield* put(actionClearFileMultiSelect());
  yield* put(
    actionSetBatchOperation({
      kind: 'trash',
      caseId,
      total: files.length,
      remaining,
    })
  );
  yield* delay(MULTI_SELECT_EXIT_ANIMATION_MS);

  try {
    for (const file of files) {
      const snapshot = fileSnapshots.get(file.tdoId);
      const ok = yield* performDeleteFile(
        {
          tdoId: file.tdoId,
          fileName: file.fileName,
          type: VirtualFolderNames.INPUT,
          isSoftDelete,
          suppressSnackbar: true,
          // VE-26943: a batch trashed out of Unassigned Media has to refresh the
          // SDO list afterwards — nothing else re-reads it, so the rows would
          // reappear on the next render.
          isLegacyTdo: isUnassignedFile(snapshot),
        },
        {
          file: snapshot,
          requestFolderPath: requestFolderPath.length
            ? requestFolderPath
            : undefined,
        }
      );
      if (ok) {
        succeeded += 1;
        if (!isSoftDelete && isUnassignedFile(snapshot)) {
          // VE-26943: a hard delete removes the TDO outright, so its
          // redact-state SDO has to go too. Left behind it keeps the file in
          // the unassigned list forever AND points the enrichment query at a
          // dead id — which fails the whole 20-id batch it lands in, taking 19
          // healthy files' names down with it.
          yield* put(
            actionDeleteMediaFileSdo({
              sdoId: snapshot.sdoId,
              tdoId: file.tdoId,
              tdoName: file.fileName,
              isMoveDeleteRequest: true,
              isSoftDelete: false,
            })
          );
        }
      } else {
        // Un-hide a file whose delete failed so it reappears in the list.
        failed += 1;
        yield* put(actionReleasePendingRemovalFiles([file.tdoId]));
      }
      remaining -= 1;
      yield* put(
        actionSetBatchOperation({
          kind: 'trash',
          caseId,
          total: files.length,
          remaining,
        })
      );
    }
  } finally {
    // Always clear the hidden set + progress, even if a delete threw, so rows
    // can never be left permanently hidden. Successful rows are already gone
    // from state (per-file DELETE_FILE_SUCCESS); this leaves only the released
    // failures.
    yield* put(actionClearPendingRemovalFiles());
    yield* put(actionSetBatchOperation(null));
  }

  if (succeeded > 0) {
    const successMessage = isSoftDelete
      ? intl.formatMessage(
          {
            id: 'batchTrashFilesSuccess',
            defaultMessage:
              '{count, plural, one {# file moved to trash.} other {# files moved to trash.}}',
          },
          { count: succeeded }
        )
      : intl.formatMessage(
          {
            id: 'batchDeleteFilesSuccess',
            defaultMessage:
              '{count, plural, one {# file deleted.} other {# files deleted.}}',
          },
          { count: succeeded }
        );
    yield* put(
      enqueueSnackbar({ message: successMessage, variant: 'success' })
    );
  }
  if (failed > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'batchTrashFilesFailure',
            defaultMessage: '{count} files could not be removed.',
          },
          { count: failed }
        ),
        variant: 'error',
      })
    );
  }
}

// VE-25098: move a set of multi-selected input files into another case/folder.
// Uses private result action types so no per-file snackbar/reducer fires. Moved
// rows are removed optimistically for instant UX, then the source case is
// refetched to reconcile counts/pagination with the server.
export function* batchMoveFilesRequest(
  action: Action<ActionBatchMoveFilesPayload, undefined>
) {
  const { files, newFolderId, oldFolderId } = action.payload;
  const intl = sagaIntl();
  const caseId = yield* select(selectMultiSelectCaseId);
  const moveActionTypes = [
    'BATCH_MOVE_FILE_INIT',
    'BATCH_MOVE_FILE_SUCCESS',
    'BATCH_MOVE_FILE_FAILURE',
  ] as const;
  const movedIds: TDOId[] = [];
  let succeeded = 0;
  let failed = 0;
  let remaining = files.length;

  // Mark the selected rows for removal (triggers their exit animation) and drop
  // the selection, then wait out the animation before finalizing removal. The
  // move itself is payload-driven, so it completes in the background regardless
  // of navigation.
  // VE-26943: snapshot each file before the selection is cleared, the same way
  // batchTrashFilesRequest does. Unassigned Media files need two things the
  // batch payload can't carry: their OWN parent folder (the payload's single
  // oldFolderId is the source folder, which for that folder is a sentinel and
  // not a real folder at all), and their redact-state SDO id, which has to be
  // deleted for the file to actually leave the folder — the SDO's existence is
  // what makes a file unassigned, so moving the TDO alone would list it in both
  // places.
  const fileSnapshots = new Map<string, AssetFileType | undefined>();
  for (const file of files) {
    fileSnapshots.set(
      file.tdoId,
      yield* select(selectSelectedTdoFile(file.tdoId))
    );
  }

  yield* put(actionSetPendingRemovalFiles(files.map((f) => f.tdoId)));
  yield* put(actionClearFileMultiSelect());
  yield* put(
    actionSetBatchOperation({
      kind: 'move',
      caseId,
      total: files.length,
      remaining,
    })
  );
  yield* delay(MULTI_SELECT_EXIT_ANIMATION_MS);

  try {
    for (const file of files) {
      const snapshot = fileSnapshots.get(file.tdoId);
      const sourceFolderId = isUnassignedFile(snapshot)
        ? snapshot.folders?.[0]?.id
        : oldFolderId;
      // VE-27388: a file that is unassigned AND unfiled has no source folder at
      // all, and moveTdo declares `$oldFolderId: ID!` — passing undefined fails
      // the mutation before it reaches the server. Pick the query the same way
      // the single-file path does (fileTdoWithCaseRequest): no source folder
      // means "file it into the request", not "move it between two".
      const query = isEmpty(sourceFolderId)
        ? fileTdoWithCaseQuery
        : moveTdoQuery;
      yield* put(
        Services.invokeGqlCall<
          MoveTdoQueryResponse | FileTdoWithCaseQueryResponse
        >(
          query,
          {
            tdoId: file.tdoId,
            tdoName: file.fileName,
            newFolderId,
            oldFolderId: sourceFolderId,
          },
          moveActionTypes
        )
      );
      const result: { type: string } = yield* take([
        moveActionTypes[1],
        moveActionTypes[2],
      ]);
      if (result.type === moveActionTypes[1]) {
        succeeded += 1;
        movedIds.push(file.tdoId);
        if (isUnassignedFile(snapshot)) {
          // Filed into a request, so it is no longer unassigned — drop the
          // redact-state SDO, exactly as the single-file move does via
          // fileTdoWithCaseSuccess.
          yield* put(
            actionDeleteMediaFileSdo({
              sdoId: snapshot.sdoId,
              tdoId: file.tdoId,
              tdoName: file.fileName,
              isMoveDeleteRequest: true,
              isSoftDelete: false,
            })
          );
        }
      } else {
        // Un-hide a file whose move failed so it reappears in the list.
        failed += 1;
        yield* put(actionReleasePendingRemovalFiles([file.tdoId]));
      }
      remaining -= 1;
      yield* put(
        actionSetBatchOperation({
          kind: 'move',
          caseId,
          total: files.length,
          remaining,
        })
      );
    }
  } finally {
    // Remove the moved rows in place (instant, no refetch flash) and always
    // clear the hidden set + progress so rows can't be left permanently hidden
    // on error.
    if (movedIds.length > 0) {
      yield* put(actionRemoveCaseInputFiles(movedIds));
    }
    yield* put(actionClearPendingRemovalFiles());
    yield* put(actionSetBatchOperation(null));
  }

  // Reconcile the source case with the server (file count, fetch-more offset /
  // availability) that the optimistic in-place removal above can't update.
  // VE-26943: skip for Unassigned Media — it has no folder-contents call, and
  // the SDO deletes above already refresh its list (deleteMediaFileSdoSuccess).
  if (succeeded > 0 && oldFolderId && !isUnassignedFolderId(oldFolderId)) {
    const limit = yield* select(selectDefaultLimit);
    const offset = yield* select(selectDefaultOffset);
    yield* put(
      actionSetSelectedFolderSuccess({
        folderId: oldFolderId,
        limit,
        offset,
        isCase: true,
      })
    );
  }

  if (succeeded > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'batchMoveFilesSuccess',
            defaultMessage: '{count} files moved successfully.',
          },
          { count: succeeded }
        ),
        variant: 'success',
      })
    );
  }
  if (failed > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'batchMoveFilesFailure',
            defaultMessage: '{count} files could not be moved.',
          },
          { count: failed }
        ),
        variant: 'error',
      })
    );
  }
}

function* updateRedactionStatusRequest(
  action: Action<ActionUpdateRedactionStatusRequestPayload, undefined>
) {
  yield* performUpdateRedactionStatus(action.payload);
}

// Core single-file status update, reused by the UPDATE_REDACTION_STATUS_REQUEST
// handler and the batch-status saga (VE-25098). Awaits the result and returns
// whether it succeeded, so the batch caller can sequence files and report one
// summary. `suppressSnackbar` rides in the GraphQL variables and is read back
// in updateRedactionStatus{Success,Failure} to skip the per-file toast.
/** The `in redaction` status tag out of a TDO's echoed details.tags (VE-26730). */
const findInRedactionStatusTag = (
  tags: unknown
):
  | {
      readonly redactionStatus?: unknown;
      readonly redactionStatusKey?: unknown;
    }
  | undefined => {
  if (!Array.isArray(tags)) {
    return undefined;
  }
  return (
    tags as {
      value?: unknown;
      redactionStatus?: unknown;
      redactionStatusKey?: unknown;
    }[]
  ).find((tag) => tag?.value === IN_REDACTION_TAG_KEY);
};

export function* performUpdateRedactionStatus(
  payload: ActionUpdateRedactionStatusRequestPayload,
  // Like performDeleteFile — the batch-status saga snapshots each file up front
  // so navigation can't strand the remaining updates.
  preFetched?: { file: AssetFileType | undefined }
) {
  const actionTypes = [
    UPDATE_REDACTION_STATUS_INIT,
    UPDATE_REDACTION_STATUS_SUCCESS,
    UPDATE_REDACTION_STATUS_FAILURE,
  ] as const;

  const {
    tdoId,
    redactionStatus,
    redactionStatusLabel,
    redactionStatusKey,
    suppressSnackbar,
    note,
    response,
    responseType,
  } = payload;
  const selectedFile = preFetched
    ? preFetched.file
    : yield* select(selectSelectedTdoFile(tdoId));
  if (!selectedFile) {
    return false;
  }

  // Persist the human name + custom UUID (VE-24546). The picker supplies both;
  // a caller that knows only the identity (e.g. legacy) derives a built-in
  // label. Drops any stale key on a custom → built-in change.
  const statusWriteFields = redactionStatusLabel
    ? {
        redactionStatus: redactionStatusLabel,
        ...(redactionStatusKey ? { redactionStatusKey } : {}),
      }
    : getBuiltInRedactionStatusTagWriteFields(redactionStatus);

  const existingTags = [...(selectedFile.details?.tags || [])];
  const statusTagIndex = existingTags.findIndex(
    (tag) => tag?.value === IN_REDACTION_TAG_KEY
  );

  // The folder-view tag array is typed as a loose index-signature Record, which
  // a concrete object type doesn't structurally satisfy — hence the cast.
  type ExistingTag = (typeof existingTags)[number];
  if (statusTagIndex >= 0) {
    existingTags[statusTagIndex] = mergeRedactionStatusTagFields(
      { ...(existingTags[statusTagIndex] || {}), value: IN_REDACTION_TAG_KEY },
      statusWriteFields
    ) as unknown as ExistingTag;
  } else {
    existingTags.push(
      mergeRedactionStatusTagFields(
        { value: IN_REDACTION_TAG_KEY },
        statusWriteFields
      ) as unknown as ExistingTag
    );
  }

  const variables = {
    tdoId,
    details: {
      ...(selectedFile.details || {}),
      tags: existingTags,
    },
    suppressSnackbar,
  };

  yield* put(
    Services.callIfTdoIsUnlocked({
      tdoId,
      fileName: lockRefusalFileName(selectedFile, tdoId),
      intent: 'update',
      call: () =>
        Services.invokeGqlCall<UpdateRedactionStatusQueryResponse>(
          updateRedactionStatusQuery,
          variables,
          actionTypes
        ),
    })
  );

  // Await THIS file's terminal action, matched on the mutation's own echoed
  // variables (meta.variables.tdoId) — pattern: folder deletes above. A bare
  // array-of-types take would match on type alone, and redux-saga's channel is
  // multicast, so a concurrent change to another file (a single-row change made
  // during a bulk run, or two quick changes) would wake the wrong taker:
  // `succeeded` misreported, and a fabricated history row recorded below for a
  // transition that never happened while the real one goes unrecorded.
  // TDO_IN_USE stays uncorrelated by exception — what it carries (two message
  // ids, and the LOCKED file's name and id) is not a tdoId this change could
  // be matched on — so a foreign lock bounce can at worst suppress one row,
  // never fabricate one.
  //
  // The `delay` is a safety backstop: callIfTdoIsUnlocked can return WITHOUT
  // emitting any terminal action (its `!schemaId` early return), which would
  // otherwise park this saga forever — and, in a batch, stall every file behind
  // it. On timeout nothing is recorded and the change is reported failed.
  const raceResult = yield* race({
    outcome: take(
      (candidate: {
        type: string;
        meta?: { variables?: { tdoId?: string } };
      }) =>
        candidate.type === TDO_IN_USE.type ||
        ((candidate.type === UPDATE_REDACTION_STATUS_SUCCESS.type ||
          candidate.type === UPDATE_REDACTION_STATUS_FAILURE.type) &&
          candidate.meta?.variables?.tdoId === tdoId)
    ),
    timeout: delay(UPDATE_STATUS_OUTCOME_TIMEOUT_MS),
  });
  // The predicate take is typed `Action` by the effect; re-assert the shape the
  // predicate already guaranteed (same idiom as Flow 1's successAction cast).
  const result = raceResult.outcome as
    | {
        type: string;
        meta?: {
          variables?: { tdoId?: string };
          response?: {
            data?: { updateTDO?: { details?: { tags?: unknown } } };
          };
        };
      }
    | undefined;
  const succeeded = result?.type === UPDATE_REDACTION_STATUS_SUCCESS.type;

  // VE-26730: record the transition here, where we hold this file's own request
  // (its payload AND its note) together with its own correlated terminal action.
  //
  // Deliberately NOT a separate watcher racing the success action: a watcher
  // could not correlate this request's note with its terminal action, and a
  // locked file that fires no terminal action at all would leave one parked
  // indefinitely, to be woken by an unrelated later change.
  //
  // The note is user-authored case text and travels only in redux; it must never
  // ride in the GraphQL variables above, which updateTDO neither declares nor
  // consumes (suppressSnackbar is a boolean flag read back off meta.variables —
  // that precedent does not extend to user content).
  if (succeeded) {
    // VE-26730: the same audit line both editors write. This surface wrote
    // none, so a status changed from a folder row — or a bulk change across
    // fifty of them — left no trace in the file's audit log at all. It cannot
    // use mediaDetails' emitter: that one reads its tdoId from the OPEN editor
    // file and returns without emitting when there is none, which here is
    // always. Hence the explicit-id helper.
    //
    // The human-readable name, never the identity: for a custom status that is
    // a UUID (VE-27621). The picker freezes the name it displayed onto
    // `redactionStatusLabel`; a caller that knows only the identity resolves
    // against the org's statuses.
    const auditCustomStatuses = yield* select(selectRedactionCustomStatuses);
    const auditLabel =
      redactionStatusLabel?.trim() ||
      resolveRedactionStatusDisplayLabel(redactionStatus, auditCustomStatuses);
    yield* put(
      emitTdoAuditEvent(
        String(tdoId),
        `Redaction Status updated to: ${auditLabel}`
      ) as never
    );

    const gate = yield* select(selectStatusHistoryGate);
    if (gate !== 'disabled') {
      // The dispatched selection is authoritative; the echoed tag is the
      // fallback for callers that pass only an identity.
      const tag = findInRedactionStatusTag(
        result?.meta?.response?.data?.updateTDO?.details?.tags
      );
      const fields =
        deriveStatusFieldsFromSelection({
          status: redactionStatus,
          statusLabel: redactionStatusLabel,
          statusKey: redactionStatusKey,
        }) || (tag ? deriveStatusFieldsFromTag(tag) : null);
      if (fields) {
        yield* put(
          actionRecordStatusHistory({
            tdoId,
            ...fields,
            isSystem: false,
            ...(note ? { note } : {}),
            ...(response ? { response } : {}),
            ...(responseType ? { responseType } : {}),
          })
        );
      }
    }
  }
  return succeeded;
}

// VE-25098: apply one redaction status to all multi-selected input files.
// Sequential so each per-file UPDATE_REDACTION_STATUS_SUCCESS (which updates
// the row's status) is awaited; per-file toasts are suppressed for one summary.
export function* batchUpdateStatusRequest(
  action: Action<ActionBatchUpdateStatusPayload, undefined>
) {
  const {
    tdoIds: requestedTdoIds,
    redactionStatus,
    redactionStatusLabel,
    redactionStatusKey: requestedKey,
    note,
    caseId: requestedCaseId,
    clearSelection,
  } = action.payload;
  const intl = sagaIntl();
  const customStatuses = yield* select(selectRedactionCustomStatuses);

  /**
   * THE GUARDS LIVE HERE, where the write is — not in whichever control
   * started it.
   *
   * Each caller had grown its own copy of some subset of these, so a new
   * caller had to know five separate things and the type enforced none of
   * them. The write happens once, so it is the one place that can be sure.
   *
   * DEDUPED: a duplicated id means two mutations, two audit events and two
   * status-history rows for one file, in a records-release trail. Nothing
   * guarantees the lists callers build are unique.
   *
   * TRIMMED: an untrimmed custom key is persisted as the tag's identity and
   * then resolves to nothing, so the file reads as an orphan.
   *
   * EMPTY: a zero-length batch still raised and cleared the progress band.
   */
  const tdoIds = Array.from(new Set(requestedTdoIds));
  const redactionStatusKey = requestedKey?.trim() || undefined;
  if (tdoIds.length === 0) {
    return;
  }

  /**
   * AND A CONFIRMED folder refusal, re-checked against the case the batch
   * names.
   *
   * CONFIRMED is the whole point, and the first version of this got it wrong
   * in a way that broke a shipped feature. `selectCanEditFolder` is
   * FAIL-CLOSED: a folder nobody has fetched a verdict for reads as denied.
   * Its own doc says that is "right for disabling a control (briefly inert,
   * harmless) and wrong for" asserting — which is why
   * `selectIsFolderConfirmedViewOnly` sits beside it, and why this uses that
   * one instead.
   *
   * Nothing populates `folderPermissions` outside the Home route:
   * `syncSelectedFolderPermissions` lives in `watchSetSelectedFolder`, forked
   * only by `caseMediaSaga`, and `warmCaseTreeWatchers` deliberately omits
   * it. So the fail-closed read denied two live paths. Unassigned Media,
   * permanently and for every user in an OLP org, because the sentinel folder
   * is excluded from both fetch sites by VE-26943 while `MultiSelectBar`
   * gates its status action on the role flag alone — the existing bulk-status
   * feature, silently doing nothing. And every cold open of the document
   * editor, where the warm-up delivers members but never a verdict.
   *
   * Refusing only on a CONFIRMED negative inverts that correctly: unfetched
   * means proceed, and a folder actually checked and found view-only is
   * refused. The server remains the authorization boundary either way — per
   * `selectCanEditFolder`'s own doc these mutations are authorized org-wide
   * server-side, so this is one place enforcing a UX policy, not the thing
   * that makes the write safe.
   *
   * AND IT SAYS SO. A bare `return` on a refusal is a swallowed error: the
   * reviewer presses Update on a dialog promising each file will record the
   * change, and nothing happens anywhere. The same argument the cancellation
   * branch below makes.
   *
   * The ROLE flag is deliberately not re-checked here: `selectRolePermissions`
   * derives from the open TDO's own lock and status, which is not a fact about
   * the set being written, and asking it mid-batch would answer about whatever
   * document happens to be open. That one stays the caller's.
   */
  if (
    requestedCaseId &&
    (yield* select(selectIsFolderConfirmedViewOnly, requestedCaseId))
  ) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'batchStatusUpdateNotPermitted',
          defaultMessage:
            'You do not have permission to change statuses in this folder.',
        }),
        variant: 'error',
      })
    );
    return;
  }
  // THE CALLER'S CASE, not the selection's.
  //
  // Reading `selectMultiSelectCaseId` here attributed every batch to whatever
  // happened to be selected in the folder view, which for a membership-driven
  // caller is someone else's request or nothing at all. An unattributed batch
  // is invisible to the progress band — and the band is what withdraws the
  // bulk bar's other actions while a write runs, so losing it let a second
  // batch be dispatched over the first, which `takeLatest` then cancelled
  // mid-loop.
  const caseId = requestedCaseId ?? null;
  let succeeded = 0;
  let failed = 0;
  let remaining = tdoIds.length;

  // Snapshot each file up front so navigation mid-batch can't strand the
  // remaining status updates (see performUpdateRedactionStatus `preFetched`).
  const fileSnapshots = new Map<string, AssetFileType | undefined>();
  for (const tdoId of tdoIds) {
    fileSnapshots.set(tdoId, yield* select(selectSelectedTdoFile(tdoId)));
  }

  // Only when the selection is what was acted on. A membership-driven batch
  // that cleared it would throw away an unrelated selection the user made
  // elsewhere in the folder view.
  if (clearSelection) {
    yield* put(actionClearFileMultiSelect());
  }
  yield* put(
    actionSetBatchOperation({
      kind: 'status',
      caseId,
      total: tdoIds.length,
      remaining,
    })
  );

  try {
    for (const tdoId of tdoIds) {
      const ok = yield* performUpdateRedactionStatus(
        {
          tdoId,
          redactionStatus,
          redactionStatusLabel,
          ...(redactionStatusKey ? { redactionStatusKey } : {}),
          suppressSnackbar: true,
          // VE-26730: one note, written to every selected file's history row.
          ...(note ? { note } : {}),
        },
        { file: fileSnapshots.get(tdoId) }
      );
      if (ok) {
        succeeded += 1;
      } else {
        failed += 1;
      }
      remaining -= 1;
      yield* put(
        actionSetBatchOperation({
          kind: 'status',
          caseId,
          total: tdoIds.length,
          remaining,
        })
      );
    }
  } finally {
    yield* put(actionSetBatchOperation(null));
    // A CANCELLED BATCH MUST STILL SAY WHAT IT DID.
    //
    // The summary toasts below sit after the loop, so a cancellation used to
    // end the run in total silence: some files written, some not, the progress
    // record cleared, and nothing on screen. The loop is sequential and not
    // transactional — there is nothing to roll back — so the only honest thing
    // left is to report the part that happened.
    //
    // Still reachable now that the chain is owned at app startup: `takeLatest`
    // supersedes this run when a second batch is dispatched, and teardown
    // cancels it.
    if (yield* cancelled()) {
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage(
            {
              id: 'batchStatusUpdateInterrupted',
              defaultMessage:
                '{count} of {total} files were updated before the change stopped.',
            },
            { count: succeeded, total: tdoIds.length }
          ),
          variant: 'warning',
        })
      );
    }
  }

  if (succeeded > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'batchStatusUpdateSuccess',
            defaultMessage: 'Status updated to {status} for {count} files.',
          },
          // Custom statuses store a uuid statusKey on the tag — resolve to the
          // display label so the toast never shows a raw uuid (VE-24546).
          {
            status: resolveRedactionStatusDisplayLabel(
              redactionStatus,
              customStatuses,
              redactionStatusLabel
            ),
            count: succeeded,
          }
        ),
        variant: 'success',
      })
    );
  }
  if (failed > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'batchStatusUpdateFailure',
            defaultMessage: '{count} files could not be updated.',
          },
          { count: failed }
        ),
        variant: 'error',
      })
    );
  }
}

/**
 * VE-24693 (AC#10): apply watermark snapshot(s) to every selected document.
 *
 * For each TDO we load its redaction-data asset, append the watermark snapshots
 * to the asset's `watermarks` (preserving existing redactions + watermarks), and
 * save it back. The watermark is then burned in on that document's next export.
 * Mirrors the batch-status pattern (up-front spinner, per-file loop, summary
 * toasts); failures on one file don't abort the rest.
 */
function* batchApplyWatermarkRequest(
  action: Action<ActionBatchApplyWatermarkPayload, undefined>
) {
  const { tdoIds, watermarks } = action.payload;
  const intl = sagaIntl();
  const caseId = yield* select(selectMultiSelectCaseId);
  if (!watermarks.length || !tdoIds.length) {
    return;
  }
  const token = yield* select(selectDocToken);
  const cfg = yield* select(selectDocGraphQLConfig);

  // §K: the picker builds its snapshots from library rows, which since §K carry
  // only a 256px preview — the raster lives on the watermark's own TDO. Resolve
  // it ONCE for the whole batch rather than per document: the same artwork is
  // about to be written to every selected file, and fetching it per file would
  // multiply one download by the size of the selection.
  //
  // A mark that cannot be resolved is dropped rather than applied: the loop
  // below skips image marks with no raster anyway, so applying one would write
  // a body referencing artwork that does not exist.
  const resolved: AppliedWatermark[] = [];
  for (const wm of watermarks) {
    if (wm.type !== 'image' || wm.imageBaseDataUrl || !wm.watermarkId) {
      resolved.push(wm);
      continue;
    }
    const record = (yield* select(selectAllWatermarkRecords)).find(
      (w) => w.id === wm.watermarkId
    );
    if (!record) {
      resolved.push(wm);
      continue;
    }
    try {
      const raster = yield* call(resolveWatermarkRaster, record.data, {
        token,
        apiRoot: cfg.apiRoot,
        graphQLEndpoint: cfg.graphQLEndpoint,
        veritoneAppId: cfg.veritoneAppId,
      });
      resolved.push(raster ? { ...wm, imageBaseDataUrl: raster } : wm);
    } catch (err) {
      console.warn(
        'Watermark artwork could not be loaded for batch apply',
        err
      );
      resolved.push(wm);
    }
  }

  let succeeded = 0;
  let failed = 0;
  let remaining = tdoIds.length;

  yield* put(actionClearFileMultiSelect());
  yield* put(
    actionSetBatchOperation({
      kind: 'watermark',
      caseId,
      total: tdoIds.length,
      remaining,
    })
  );

  try {
    for (const tdoId of tdoIds) {
      try {
        const loaded = yield* call(
          serviceLoadDocumentRedactionData,
          tdoId,
          token,
          cfg.apiRoot,
          cfg.graphQLEndpoint,
          cfg.veritoneAppId
        );
        // Asset format 1.3.0: an image mark's raster lives in its own
        // `document-watermark-image` asset on the SAME TDO, so each document in
        // the batch needs its own copy stored before the body that references it
        // is written. Without this the batch would persist image marks with no
        // artwork — the body no longer carries the raster inline, and this path
        // never goes through the editor saga that uploads it.
        //
        // Only for marks this document does not already have: the append below
        // skips a definition that is already applied, and uploading first would
        // leave an orphaned asset behind on every re-run of the same batch.
        const already = new Set(
          // The version here is never persisted: this is a synthetic EMPTY body
          // standing in for a document with no asset yet, fed to a READ so the
          // no-asset case needs no second branch. `appendWatermarksToAssetBody`
          // below stamps the version on anything actually written. Worth saying
          // because this is the one place outside the documents module that
          // names the constant, and it now reads 1.5.0 — the lexicon format —
          // on a path that has nothing to do with lexicons (review nit N17).
          watermarksFromAssetBody(
            loaded?.body ?? {
              assetFormatVersion: DOCUMENT_REDACTION_ASSET_FORMAT_VERSION,
              tdoId,
              redactions: {},
            }
          )
            .map((w) => w.watermarkId)
            .filter((id): id is string => !!id)
        );
        const stored: AppliedWatermark[] = [];
        for (const wm of resolved) {
          if (
            wm.type !== 'image' ||
            !wm.imageBaseDataUrl ||
            wm.imageAssetId ||
            (wm.watermarkId && already.has(wm.watermarkId))
          ) {
            stored.push(wm);
            continue;
          }
          // Reuse before upload, same as the editor path: this document may
          // already carry this watermark's raster from an earlier apply, an
          // earlier batch, or a session the user abandoned. Nothing deletes
          // these, so finding one is the normal case on a re-run.
          const existing = yield* call(
            serviceFindWatermarkImageAsset,
            tdoId,
            wm.watermarkId,
            wm.imageBaseDataUrl,
            token,
            cfg.apiRoot,
            cfg.graphQLEndpoint,
            cfg.veritoneAppId
          );
          const assetId =
            existing ??
            (yield* call(
              serviceSaveWatermarkImageAsset,
              tdoId,
              wm.imageBaseDataUrl,
              wm.imageName,
              wm.watermarkId,
              token,
              cfg.apiRoot,
              cfg.graphQLEndpoint
            )).id;
          stored.push({ ...wm, imageAssetId: assetId });
        }
        // Through the documents module's own writer, not assembled here: it
        // owns this schema, stamps the current format version, and strips the
        // rasters that must never be persisted beside their own reference.
        const newBody = appendWatermarksToAssetBody(
          loaded?.body,
          tdoId,
          stored
        );
        yield* call(
          serviceSaveDocumentRedactionData,
          tdoId,
          newBody,
          token,
          cfg.apiRoot,
          cfg.graphQLEndpoint
        );
        succeeded += 1;
      } catch {
        failed += 1;
      }
      remaining -= 1;
      yield* put(
        actionSetBatchOperation({
          kind: 'watermark',
          caseId,
          total: tdoIds.length,
          remaining,
        })
      );
    }
  } finally {
    yield* put(actionSetBatchOperation(null));
  }

  if (succeeded > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'batchWatermarkSuccess',
            defaultMessage: 'Watermark added to {count} files.',
          },
          { count: succeeded }
        ),
        variant: 'success',
      })
    );
  }
  if (failed > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'batchWatermarkFailure',
            defaultMessage: '{count} files could not be updated.',
          },
          { count: failed }
        ),
        variant: 'error',
      })
    );
  }
}

function* updateRedactionStatusSuccess(
  action: ReturnType<typeof UPDATE_REDACTION_STATUS_SUCCESS>
) {
  const tdoId = action.payload.updateTDO.id;
  // TODO clean typing for tags
  const tags = action.meta?.response?.data?.updateTDO?.details?.tags as
    | Array<{
        value?: string;
        redactionStatus?: string;
        redactionStatusKey?: string;
      }>
    | undefined; // .find(tag: any => tag?.value === IN_REDACTION_TAG_KEY)?.redactionStatus;

  const statusTag = tags?.find((tag) => tag?.value === IN_REDACTION_TAG_KEY);
  const redactionStatus = statusTag
    ? getRedactionStatusIdentity(statusTag)
    : undefined;
  const redactionStatusName = statusTag?.redactionStatus;

  if (!isRedactionStatus(redactionStatus)) {
    return;
  }
  const intl = sagaIntl();

  // Always update the row's status; the batch saga (VE-25098) shows one summary
  // toast instead of a per-file one.
  yield* put(
    actionUpdateRedactionStatusChange({
      tdoId,
      redactionStatus,
      redactionStatusLabel: redactionStatusName,
      ...(statusTag?.redactionStatusKey
        ? { redactionStatusKey: statusTag.redactionStatusKey }
        : {}),
    })
  );
  const { suppressSnackbar } = (action.meta?.variables ?? {}) as {
    suppressSnackbar?: boolean;
  };
  if (suppressSnackbar) {
    return;
  }
  const customStatuses = yield* select(selectRedactionCustomStatuses);
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage(
        {
          id: 'statusUpdatedSuccess',
          defaultMessage: 'Status updated to {status} successfully.',
        },
        // Custom statuses store a uuid statusKey on the tag — resolve to the
        // display label so the toast never shows a raw uuid (VE-24546).
        {
          status: resolveRedactionStatusDisplayLabel(
            redactionStatus,
            customStatuses,
            redactionStatusName
          ),
        }
      ),
    })
  );
}

function* updateRedactionStatusFailure(
  action: ReturnType<typeof UPDATE_REDACTION_STATUS_FAILURE>
) {
  const { suppressSnackbar } = (action.meta?.variables ?? {}) as {
    suppressSnackbar?: boolean;
  };
  if (suppressSnackbar) {
    return;
  }
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'statusUpdateFailed',
        defaultMessage: 'Status update failed.',
      }),
      variant: 'error',
    })
  );
}

function* createExportTdoRequest(
  action: Action<{ treeObjectId: TreeObjectId }, undefined>
) {
  const actionTypes = [
    CREATE_EXPORT_TDO_INIT,
    CREATE_EXPORT_TDO_SUCCESS,
    CREATE_EXPORT_TDO_FAILURE,
  ] as const;
  const variables = {
    input: {
      parentFolderId: action.payload.treeObjectId,
      startDateTime: moment(new Date()).toISOString(),
      stopDateTime: moment(new Date()).toISOString(),
      details: {
        isExport: true,
      },
    },
  };
  const query = createExportFilesQuery;
  yield* put(
    Services.invokeGqlCall<CreateExportFilesQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

function* fetchExportTdoSuccess(
  action: Action<
    FetchExportTdoQueryResponse,
    { variables: { treeObjectId: TreeObjectId } }
  >
) {
  const url =
    action.payload?.temporalDataObject?.assets?.records?.[0]?.signedUri;
  if (!url) {
    return;
  }

  const treeObjectId = action.meta?.variables?.treeObjectId;
  const folders: FolderStateType[] = yield* select(selectSelectedFolders);
  const folder = folders.find((f) => f.treeObjectId === treeObjectId);

  const intl = sagaIntl();
  try {
    yield* call(downloadNow, url, folder?.name);
  } catch (error) {
    console.error('Export TDO download failed', error);
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'downloadFailure',
          defaultMessage:
            'Unexpected error occurred while downloading file. Please try again later.',
        }),
        variant: 'error',
      })
    );
  }
}

function* fetchExportTdoRequest(action: Action<ExportTdoRequest>) {
  const actionTypes = [
    FETCH_EXPORT_TDO_INIT,
    FETCH_EXPORT_TDO_SUCCESS,
    FETCH_EXPORT_TDO_FAILURE,
  ] as const;
  const { exportTdoId, treeObjectId } = action.payload;
  const variables = {
    tdoId: exportTdoId,
    treeObjectId,
  };
  const query = fetchExportTdoQuery;
  yield* put(
    Services.invokeGqlCall<FetchExportTdoQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

function* createExportTdoSuccess(
  // action: Action<
  //   { createTDO: { id: TDOId } },
  //   { variables: { input: { parentFolderId: TreeObjectId } } }
  // >
  action: ReturnType<typeof CREATE_EXPORT_TDO_SUCCESS>
) {
  const exportTdoId = action.payload?.createTDO?.id;
  const treeObjectId = action.meta.variables?.input?.parentFolderId;
  yield* put(actionExportFilesRequest({ treeObjectId, exportTdoId }));
}

function* exportFilesRequest(action: Action<ExportFilesRequest>) {
  const { defaultClusterId, downloadEngineId } =
    yield* select(selectConfigEngines);
  const actionTypes = [
    EXPORT_FILES_INIT,
    EXPORT_FILES_SUCCESS,
    EXPORT_FILES_FAILURE,
  ] as const;
  const { treeObjectId, exportTdoId } = action.payload;
  const variables = {
    caseId: treeObjectId,
    tdoId: exportTdoId,
    clusterId: defaultClusterId ?? '',
    engineId: downloadEngineId,
  };
  const query = exportFilesQuery;
  yield* put(
    Services.invokeGqlCall<ExportFilesQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

function* exportFilesSuccess(
  // action: Action<
  //   { createJob: { id: string } },
  //   { variables: { tdoId: TDOId; caseId: TreeObjectId } }
  // >
  action: ReturnType<typeof EXPORT_FILES_SUCCESS>
) {
  const { tdoId, caseId } = action.meta.variables;
  const { createJob } = action.payload;
  yield* put(
    actionDownloadAllJobStatusRequest({
      jobId: createJob?.id,
      exportTdoId: tdoId,
      treeObjectId: caseId,
    })
  );
}

function* downloadAllJobStatusRequest(
  action: Action<DownloadAllJobStatusRequest, undefined>
) {
  const actionTypes = [
    FETCH_DOWNLOADALL_JOB_STATUS_INIT,
    FETCH_DOWNLOADALL_JOB_STATUS_SUCCESS,
    FETCH_DOWNLOADALL_JOB_STATUS_FAILURE,
  ] as const;
  const { jobId, exportTdoId, treeObjectId } = action.payload;
  const variables = { jobId, exportTdoId, treeObjectId };
  const query = fetchJobStatusQuery;
  yield* put(
    Services.invokeGqlCall<FetchJobStatusQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

function* downloadAllJobStatusSuccess(
  // action: Action<
  //   { job: { id: string; status: string } },
  //   { variables: { exportTdoId: TDOId; treeObjectId: TreeObjectId } }
  // >
  action: ReturnType<typeof FETCH_DOWNLOADALL_JOB_STATUS_SUCCESS>
) {
  const { exportTdoId, treeObjectId } = action.meta.variables;
  const { id, status } = action.payload.job;
  if (status === 'failed') {
    yield* put(
      actionDownloadAllStatus({
        status: false,
        treeObjectId: treeObjectId,
      })
    );
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'downloadAllJobFailure',
          defaultMessage:
            'Unexpected error occurred while downloading all output files. Please try again later.',
        }),
        variant: 'error',
      })
    );
  } else if (status === 'complete') {
    yield* put(actionFetchExportTdoRequest({ exportTdoId, treeObjectId }));
  } else {
    window.aiware?.auth?.reportAppActivity?.();
    yield* delay(5000);
    yield* put(
      actionDownloadAllJobStatusRequest({
        jobId: id,
        exportTdoId,
        treeObjectId,
      })
    );
  }
}

function* fetchMoreRootFolderRequest(
  action: Action<FetchRootFolderRequest, undefined>
) {
  yield* syncRootFolderSmartSelection();
  yield* put(
    Services.fetchRootFolders(action.payload, [
      FETCH_MORE_ROOTFOLDERS_INIT,
      FETCH_MORE_ROOTFOLDERS_SUCCESS,
      FETCH_MORE_ROOTFOLDERS_FAILURE,
    ])
  );
}

function* fetchMoreSubFoldersRequest(
  action: Action<FetchSubfolderRequest, undefined>
) {
  const actionTypes = [
    FETCH_MORE_SUBFOLDERS_INIT,
    FETCH_MORE_SUBFOLDERS_SUCCESS,
    FETCH_MORE_SUBFOLDERS_FAILURE,
  ] as const;
  const { folderId, limit, offset } = action.payload;
  const variables = { folderId: folderId, offset, limit };
  const query = fetchSubFoldersQuery;
  yield* put(
    Services.invokeGqlCall<FetchSubFoldersQueryResponse>(
      query,
      variables,
      actionTypes,
      { returnPartialDataOnNotFound: true }
    )
  );
}

function* moveFolderRequest(action: Action<MoveFolderRequest, undefined>) {
  const actionTypes = [
    MOVE_FOLDER_INIT,
    MOVE_FOLDER_SUCCESS,
    MOVE_FOLDER_FAILURE,
  ] as const;
  const {
    treeObjectId,
    oldParentTreeId,
    newParentTreeId,
    folderId,
    oldParentId,
    newParentId,
    isRoot,
    suppressSnackbar,
  } = action.payload;
  // VE-21114: v2 authorizes moveFolder via OLP against the folder ids passed to
  // folderId/fromFolderId/toFolderId; the deprecated treeObject ids return
  // not_allowed. treeObjectId is kept in the variables for state correlation
  // (reducers/matchers read meta.variables.treeObjectId); v1 falls back to the
  // treeObject ids for the mutation inputs.
  const isV2Folders = yield* select(selectV2FoldersEnabled);
  const variables = {
    treeObjectId,
    isRoot,
    suppressSnackbar,
    folderId: resolveFolderMutationId(isV2Folders, { folderId, treeObjectId }),
    fromFolderId: resolveFolderMutationId(isV2Folders, {
      folderId: oldParentId,
      treeObjectId: oldParentTreeId,
    }),
    toFolderId: resolveFolderMutationId(isV2Folders, {
      folderId: newParentId,
      treeObjectId: newParentTreeId,
    }),
  };

  const query = moveFolderOrCaseQuery;
  yield* put(
    Services.invokeGqlCall<MoveFolderOrCaseQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

function* moveFolderSuccess(action: ReturnType<typeof MOVE_FOLDER_SUCCESS>) {
  const intl = sagaIntl();
  const { isRoot, suppressSnackbar } = action.meta.variables;
  const offset = yield* select(selectDefaultOffset);
  const limit = yield* select(selectDefaultLimit);
  const selectedFolderIds: (FolderId | undefined)[] = yield* select(
    selectSelectedFolderIds
  );
  if (isRoot) {
    // VE-26833: see refetchAllLoadedRootFolders — moving a root folder must not
    // collapse the list back to page 1.
    yield* refetchAllLoadedRootFolders();
  }
  if (selectedFolderIds && selectedFolderIds?.length > 0) {
    for (const selectedId of selectedFolderIds) {
      if (selectedId) {
        yield* put(
          actionSetSelectedFolderSuccess({
            folderId: selectedId,
            limit,
            offset,
            isCase: false,
          })
        );
      }
    }
  }
  if (!suppressSnackbar) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'moveFolderSuccess',
          defaultMessage: 'Moving folder or request is successful.',
        }),
        variant: 'success',
      })
    );
  }
}

function* moveFolderFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'moveFolderFailure',
        defaultMessage: 'Failed moving folder or request.',
      }),
      variant: 'error',
    })
  );
}

function* fetchSelectedCaseOrFolderTreeRequest(
  action: Action<{ folderId: string; isCase: boolean }>
) {
  const actionTypes = [
    FETCH_SELECTED_CASE_OR_FOLDER_TREE_INIT,
    FETCH_SELECTED_CASE_OR_FOLDER_TREE_SUCCESS,
    FETCH_SELECTED_CASE_OR_FOLDER_TREE_FAILURE,
  ] as const;
  const { folderId, isCase } = action.payload;
  const limit = yield* select(selectDefaultLimit);
  const offset = yield* select(selectDefaultOffset);
  const variables = { folderId, limit, offset, isCase };
  const query = fetchCaseOrFolderTreeQuery;
  yield* put(
    Services.invokeGqlCall<FetchCaseOrFolderTreeQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

export function* fetchSelectedCaseOrFolderTreeSuccess(
  action: Action<
    { folder: FolderStateType },
    { variables: { folderId: FolderId; isCase: boolean } }
  >
) {
  const { folderId, isCase } = action.meta.variables;
  const { folderPath } = action.payload.folder;

  // VE-26118: moving a folder/request to Trash only reparents it, so it still
  // resolves here — select nothing instead of building columns from a folderPath
  // that contains the system trash folder (which would render Trash as a column
  // holding the trashed item). Aborting beats filtering the trash entry out:
  // that would leave the trashed item itself as a root-level column, no less
  // wrong. Also forget it as the remembered last-open folder, or the next page
  // load would restore straight back into Trash with no nav-to involved.
  if (isFolderInTrash(action.payload.folder)) {
    const orgId = yield* select(selectOrganizationId);
    if (readStoredLastOpenFolder(orgId)?.id === folderId) {
      persistLastOpenFolder(orgId, null);
    }
    yield* put(actionClearNavCase());
    return;
  }

  const rootFolderId = yield* select(selectRootFolderId);
  // VE-21114: v1 returned the queried folder itself as the leaf of its own
  // `folderPath`; v2 excludes it. The chain below drives the column stack (and
  // only its DEEPEST entry gets its subfolders fetched — SET_SELECTED_FOLDER is
  // takeLatest), so on v2 the raw path stops at the PARENT: navigating to a
  // request from a search result opened its parent folder instead. Re-attach the
  // leaf. Its level is the ancestor count — one deeper than every ancestor — so
  // it sorts last here, and the reducer positions its column deepest.
  const isV2Folders = yield* select(selectV2FoldersEnabled);
  const ancestors = folderPath ?? [];
  const folderChain =
    isV2Folders && !ancestors.some((f) => f.id === folderId)
      ? [...ancestors, action.payload.folder]
      : ancestors;
  // Not `folderPath.length`: under v2 a top-level folder/request has NO
  // ancestors, and the old guard skipped it entirely — selecting nothing at all.
  if (folderChain.length > 0) {
    const parents = _sortBy(
      folderChain
        .filter((f) => f.id !== rootFolderId)
        .map((f) => ({ ...f, level: f.folderPath?.length || 0 })),
      ['level']
    );
    // VE-28429: restoring the column chain is navigation, not a change — this
    // handler runs on the editor's return (CaseFolders consumes `editorReturnTarget`
    // on mount), on a deep link and on the last-open restore, none of which means
    // "something changed, go and look again". So both contents fetches below opt
    // in to reuse; `isCaseContentsFresh` still refuses on a first load, a
    // different folder, or rows we no longer hold, which is what makes deep links
    // and post-reload restores fetch anyway.
    //
    // BOTH matter. The chain loop's deepest entry carries `isCase`, so it reaches
    // the contents fetch through `setSelectedFolder`; the explicit success below
    // reaches `fetchSubFolders` directly. Flagging only one leaves the other
    // refetching, and the second one wins — which is how the skip came to look
    // like it had never worked.
    for (const parent of parents) {
      yield* put(
        actionSetSelectedFolder({
          folder: parent,
          isCase: isCase && parent.id === folderId,
          reuseFreshContents: true,
        })
      );
    }
    if (isCase) {
      const defaultLimit = yield* select(selectDefaultLimit);
      const defaultOffset = yield* select(selectDefaultOffset);
      yield* put(
        actionSetSelectedFolderSuccess({
          folderId: folderId,
          limit: defaultLimit,
          offset: defaultOffset,
          isCase: isCase,
          reuseFreshContents: true,
        })
      );
      // Disabled automatic virtual folder selection for unified folder view
      // Files are now shown directly in the case folder
      /*
      yield* takeLatest(FETCH_SUB_FOLDERS_BYID_SUCCESS, function* () {
        const selectedCase = (yield* select(selectSelectedFolders))?.at(-1);
        if (selectedCase && checkIsCaseId(selectedCase)) {
          yield* put(
            actionSetSelectedVirtualFolder({
              virtualFolderName: VirtualFolderNames.INPUT,
              folder: selectedCase,
            })
          );
        }
      });
      */
    }
  }

  // VE-26118: a nav-to is a one-shot command — consume it now that it has been
  // acted on. ROUTE_HOME latches isNavToCaseMedia/navToId and nothing else
  // cleared them except a folder click, so CaseFolders' fetch effect replayed
  // this navigation on every root refetch — yanking the user's columns back to
  // the last-closed case, and, after a move-to-trash refetch, into Trash. (The
  // effect re-runs because the root-fetch reducers mint a new rootFolder object,
  // changing its dep by identity — selectRootFolder itself is a plain accessor,
  // so memoizing it would NOT have helped.)
  yield* put(actionClearNavCase());
}

export function* fetchSelectedCaseOrFolderTreeFailure(
  action: Action<{ name: string; message: string }[], undefined>
) {
  // A failed nav-to is spent too: without this, the latch survives and the next
  // root refetch silently retries the same dead target (VE-26118).
  yield* put(actionClearNavCase());
  if (!action.payload[0]) {
    return;
  }
  const { name, message } = action.payload[0];
  if (name === 'not_found') {
    // The target definitively no longer resolves — permanently deleted. NOT the
    // moved-to-trash case: a trashed folder is merely reparented and still
    // resolves, so it succeeds and is caught by the in-trash guard in
    // fetchSelectedCaseOrFolderTreeSuccess instead (VE-26118).
    // (Cast: callGraphQLApi attaches the request variables to failure meta,
    // but the failure action's typing doesn't carry the variables shape.)
    const failedFolderId = (
      action as unknown as { meta?: { variables?: { folderId?: string } } }
    ).meta?.variables?.folderId;
    const orgId = yield* select(selectOrganizationId);
    // Was this the remembered last-open folder being auto-restored? Then it
    // was deleted (e.g. by another user) since we saved it. Drop the stored
    // id so the failed restore isn't retried, and stay SILENTLY at root — the
    // user didn't ask for this folder, so a snackbar would just be noise.
    // ONLY suppress in that case: transient failures and user-initiated
    // deep-link fetches to a dead folder still surface the warning.
    if (
      failedFolderId &&
      readStoredLastOpenFolder(orgId)?.id === failedFolderId
    ) {
      persistLastOpenFolder(orgId, null);
      return;
    }
    yield* put(
      enqueueSnackbar({
        message,
        variant: 'warning',
      })
    );
  }
}

function* fetchLegacyMediasRequest(
  action: Action<ActionFetchLegacyMediasRequest, undefined>
) {
  yield* put(Services.fetchLegacyMedias(action.payload));
}

function* fetchLegacyMediasSuccess(
  action: Action<
    ActionLegacyMediasSuccessPayload,
    { variables: ActionFetchLegacyMediasRequest }
  >
) {
  const { structuredDataObjects } = action.payload;

  const tdoIds = (structuredDataObjects?.records || [])
    .filter((s) => s.data?.tdoId)
    .map((m) => m.data.tdoId);

  if ((tdoIds?.length || 0) > 0) {
    yield* put(actionFetchTdoDetailForSdos({ tdoIds }));
  }

  // VE-26943: this used to re-dispatch itself with `offset + limit` whenever a
  // page came back full, walking the org's ENTIRE unassigned list on every
  // dashboard mount (two round-trips per 30 files — the SDO page plus its
  // aliased TDO-detail batch). It also meant FolderColumn's scroll-driven
  // `fetchMoreLegacyMedias` never got a chance to fire. Paging is now demand
  // -driven like every other folder list; the mount fetch loads page one only,
  // which is all the pinned row / landing rule need to know the folder is
  // non-empty.
}

function* fetchLegacyMediasFailure(
  action: Action<[{ message: string }], undefined>
) {
  const message = action?.payload?.[0]?.message;
  yield* put(
    enqueueSnackbar({
      message: `Failed to fetch legacy medias:  ${message}`,
      variant: 'error',
    })
  );
}

function* fileTdoWithCaseRequest(
  action: Action<ActionFileTdoWithCasePayload, undefined>
) {
  const actionTypes = [
    FILE_TDO_WITH_CASE_INIT,
    FILE_TDO_WITH_CASE_SUCCESS,
    FILE_TDO_WITH_CASE_FAILURE,
  ] as const;
  const { sdoId, tdoName, newFolderId, tdoId, oldFolderId } = action.payload;
  const variables = {
    sdoId,
    tdoName,
    tdoId,
    newFolderId: newFolderId,
    oldFolderId: oldFolderId,
  };
  const query = isEmpty(oldFolderId) ? fileTdoWithCaseQuery : moveTdoQuery;
  yield* put(
    Services.invokeGqlCall<FileTdoWithCaseQueryResponse | MoveTdoQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

function* fileTdoWithCaseSuccess(
  // action: Action<
  //   | { fileTemporalDataObject: { id: TDOId } }
  //   | { moveTemporalDataObject: { id: TDOId } },
  //   {
  //     variables: ActionFileTdoWithCasePayload;
  //   }
  action: ReturnType<typeof FILE_TDO_WITH_CASE_SUCCESS>
) {
  const offset = yield* select(selectDefaultOffset);
  const limit = yield* select(selectDefaultLimit);
  const { sdoId, tdoName, newFolderId, tdoId } = action.meta.variables;
  const { id } =
    'moveTemporalDataObject' in action.payload
      ? action.payload.moveTemporalDataObject
      : action.payload.fileTemporalDataObject;
  if (id === tdoId) {
    yield* put(
      actionDeleteMediaFileSdo({
        sdoId,
        tdoId,
        tdoName,
        isMoveDeleteRequest: true,
        isSoftDelete: false,
      })
    );
    yield* put(
      actionSetSelectedFolderSuccess({
        folderId: newFolderId,
        limit,
        offset,
        isCase: true,
      })
    );
    // VE-27388: translated, like the moveTdo path's moveFileToCaseSuccess.
    // The Move dialog reaches this saga for Unassigned Media files, so a
    // hardcoded string here is a FR gap on a user-facing path.
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'fileTdoWithCaseSuccess',
            defaultMessage: '{fileName} moved successfully.',
          },
          { fileName: tdoName }
        ),
        variant: 'success',
      })
    );
  }
}

function* fileTdoWithCaseFailure(
  // action: Action<
  //   [{ message: string }],
  //   {
  //     variables: {
  //       tdoName: string;
  //     };
  //   }
  // >
  action: ReturnType<typeof FILE_TDO_WITH_CASE_FAILURE>
) {
  const message = action?.payload?.[0]?.message;
  const { tdoName } = action.meta.variables;
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      // The API detail is appended when there is one; `.trim()` keeps the
      // sentence from ending in a dangling space when there isn't.
      message: intl
        .formatMessage(
          {
            id: 'fileTdoWithCaseFailure',
            defaultMessage: 'Failed to move {fileName} file. {message}',
          },
          { fileName: tdoName, message: message ?? '' }
        )
        .trim(),
      variant: 'error',
    })
  );
}

function* deleteMediaFileSdoRequest(
  action: Action<ActionDeleteMediaFileSdoPayload, undefined>
) {
  const { isSoftDelete, isMoveDeleteRequest, sdoId } = action.payload;
  const trashRetentionDays = yield* select(selectTrashRetentionDays);
  let payload: ActionDeleteMediaFileSdoPayload;
  // TODO: might not need this if-else anymore
  if (isSoftDelete && !isMoveDeleteRequest) {
    const medias = yield* select(selectLegacyMedias);
    const sdoData = (medias || [])
      .filter((m) => m.sdoId === sdoId)
      .map(
        (s) =>
          ({
            tdoId: s.tdoId,
            status: s.status,
            trashRetentionDays,
            movedToTrashDateTime: moment().toISOString(),
          }) as LegacyMediaFileDataType
      )?.[0];
    payload = { ...action.payload, data: sdoData };
  } else {
    payload = { ...action.payload };
  }
  yield* put(Services.deleteMediaFileSdo(payload));
}

function* deleteMediaFileSdoSuccess(
  action: Action<ActionDeleteMediaFileSdoPayload, undefined>
) {
  const { tdoId, tdoName, isMoveDeleteRequest, isSoftDelete } = action.payload;
  if (isSoftDelete && !isMoveDeleteRequest) {
    yield* put(
      actionDeleteFile({
        tdoId: tdoId,
        fileName: tdoName,
        type: VirtualFolderNames.INPUT,
        isSoftDelete: true,
        isLegacyTdo: true,
      })
    );
  }
  // VE-26943: likewise no refetch — DELETE_MEDIA_FILE_SDO_SUCCESS removes the
  // row in place, which keeps the user's loaded pages and scroll position.
}

function* deleteMediaFileSdoFailure(
  // action: Action<[{ message: string }], { variables: { tdoName: string } }>
  action: ReturnType<typeof DELETE_MEDIA_FILE_SDO_FAILURE>
) {
  const message = action.payload?.[0]?.message ?? '';
  const { tdoName } = action.meta.variables;
  yield* put(
    enqueueSnackbar({
      message: `Delete ${tdoName} failed. ${message}`,
      variant: 'error',
    })
  );
}

function* refreshFileOutputsOnRedactionComplete(action: {
  payload: { tdoId: TDOId };
}) {
  yield* put(actionRefreshFileOutputs({ tdoIds: [action.payload.tdoId] }));
}

function* refreshFileOutputsRequest(action: { payload: { tdoIds: TDOId[] } }) {
  const [first, ...rest] = action.payload.tdoIds;
  if (!first) {
    return;
  }
  yield* put(Services.fetchFileOutputs([first, ...rest]));
}

/** Backoff before the single refresh retry — long enough for asset-read lag. */
const REFRESH_FILE_OUTPUTS_RETRY_MS = 3000;

/**
 * Files whose refresh has already been retried and not yet succeeded. Without
 * this bound a persistent failure would loop forever, refetching on its own
 * failure.
 *
 * Cleared per file by `clearRefreshFileOutputsRetry` on the next successful
 * refresh, so the bound is one retry per *run of failures* rather than one per
 * tab: a file that blips once and recovers is eligible again next time. A file
 * that keeps failing stays marked, which is the loop we are guarding against.
 */
const retriedRefreshTdoIds = new Set<string>();

/**
 * A refresh landed, so this file's failure run is over — let the next one have
 * its retry back. Reads the ids off the request meta rather than the response,
 * because the response is keyed by alias and a purged TDO is simply absent from
 * it (the read is partial-data tolerant).
 */
export const clearRefreshFileOutputsRetry = (action: {
  meta?: { variables?: unknown };
}) => {
  const tdoIds =
    (action.meta as { variables?: { tdoIds?: TDOId[] } } | undefined)?.variables
      ?.tdoIds ?? [];
  tdoIds.forEach((id) => retriedRefreshTdoIds.delete(id));
};

/**
 * VE-28061: the refresh runs AFTER the "Redacting" indicator has been cleared,
 * so a silent failure leaves the row showing whatever it loaded with — reading
 * "Original" for a file that is redacted, with nothing offered in Download
 * Review, until the folder is reloaded. It previously had no handler at all,
 * not even a log.
 *
 * Retried once per run of failures, because the likely cause is benign: the
 * assets query is read moments after `createAsset`, so a miss is usually
 * read-your-writes lag rather than a real failure. `retriedRefreshTdoIds`
 * documents when the retry becomes available to a file again.
 *
 * Deliberately no snackbar. This is background reconciliation the user did not
 * ask for, the redaction itself has already succeeded and been announced, and
 * the row self-heals on reload — a toast here would be noise about something
 * they cannot act on.
 */
export function* refreshFileOutputsFailure(action: {
  payload?: { message?: string }[];
  meta?: { variables?: unknown };
}) {
  const message = Array.isArray(action.payload)
    ? action.payload[0]?.message
    : undefined;
  const tdoIds = (
    (action.meta as { variables?: { tdoIds?: TDOId[] } } | undefined)?.variables
      ?.tdoIds ?? []
  ).filter((id) => !retriedRefreshTdoIds.has(id));
  console.warn(
    '[casemedia] refreshing file outputs after a redaction failed:',
    message ?? 'unknown error',
    tdoIds.length > 0 ? `retrying ${tdoIds.join(', ')}` : '(not retrying)'
  );
  if (tdoIds.length === 0) {
    return;
  }
  tdoIds.forEach((id) => retriedRefreshTdoIds.add(id));
  yield* delay(REFRESH_FILE_OUTPUTS_RETRY_MS);
  yield* put(actionRefreshFileOutputs({ tdoIds }));
}

function* fetchTdoDetailForSdosRequest(action: {
  payload: { tdoIds: TDOId[] };
}) {
  yield* put(Services.fetchTdoDetailForSdos(action.payload));
}

function* fetchTdoDetailForSdosFailure(
  action: Action<[{ message: string }], undefined>
) {
  const message = action.payload?.[0].message;
  yield* put(
    enqueueSnackbar({
      message: `Failed to fetch tdo details. ${message}`,
      variant: 'error',
    })
  );
}

function* renameFolderRequest(
  action: Action<ActionRenameFolderPayload, undefined>
) {
  const actionTypes = [
    RENAME_FOLDER_INIT,
    RENAME_FOLDER_SUCCESS,
    RENAME_FOLDER_FAILURE,
  ] as const;
  const { id, name } = action.payload;
  const variables = { id, name };
  const query = renameFolderQuery;
  yield* put(
    Services.invokeGqlCall<RenameFolderQueryResponse>(
      query,
      variables,
      actionTypes
    )
  );
}

function* renameFolderSuccess() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'renameSuccess',
        defaultMessage: 'Rename completed successfully.',
      }),
      variant: 'info',
    })
  );
}

function* renameFolderFailure(
  action: Action<[{ message: string }], undefined>
) {
  const message = action.payload?.[0].message;
  yield* put(
    enqueueSnackbar({
      message: `Failed to rename. ${message}`,
      variant: 'error',
    })
  );
}

function* fetchForExpandedFolderRequest(
  action: Action<ActionFetchForExportedFolderPayload, undefined>
) {
  const actionTypes = [
    FETCH_FOR_EXPANDED_FOLDER_INIT,
    FETCH_FOR_EXPANDED_FOLDER_SUCCESS,
    FETCH_FOR_EXPANDED_FOLDER_FAILURE,
  ] as const;
  const { folderId, limit, offset } = action.payload;
  const variables = { folderId, limit, offset };
  const query = fetchSubFoldersQuery;
  yield* put(
    Services.invokeGqlCall<FetchSubFoldersQueryResponse>(
      query,
      variables,
      actionTypes,
      { returnPartialDataOnNotFound: true }
    )
  );
}

function* fetchForExpandedFolderSuccess(
  action: Action<
    { folder: FolderStateType },
    { variables: ActionFetchForExportedFolderPayload }
  >
) {
  const { folder } = action.payload;
  const { folderId, limit, offset } = action.meta.variables;
  if ((folder?.childFolders?.records?.length || 0) === limit) {
    const nextOffset = offset + limit;
    yield* put(
      actionFetchForExpandedFolders({ folderId, limit, offset: nextOffset })
    );
  }
}

function* fetchForExpandedFolderFailure(
  action: Action<
    [{ message: string }],
    { variables: ActionFetchForExportedFolderPayload }
  >
) {
  const message = action.payload?.[0].message;
  yield* put(
    enqueueSnackbar({
      message: `Failed to fetch more for expanded folder. ${message}`,
      variant: 'error',
    })
  );
}

function* moveRestoredFolderFromTrash(
  treeObjectId: TreeObjectId,
  parentId: FolderId,
  folderId?: FolderId
) {
  const trashFolderId = yield* call(ensureTrashFolderId);
  if (trashFolderId) {
    yield* put(
      actionMoveFolderRequest({
        treeObjectId,
        oldParentTreeId: trashFolderId as FolderId,
        newParentTreeId: parentId,
        // VE-21114: v2 moveFolder authorizes by folder ids.
        folderId,
        oldParentId: trashFolderId as FolderId,
        newParentId: parentId,
        isRoot: false,
        suppressSnackbar: true,
      })
    );
  }
}

function* searchNameExistsInFolderRequest(
  action: Action<SearchNameExistsInFolderRequestPayload, undefined>
) {
  const actionTypes = [
    SEARCH_NAME_EXISTS_IN_FOLDER_INIT,
    SEARCH_NAME_EXISTS_IN_FOLDER_SUCCESS,
    SEARCH_NAME_EXISTS_IN_FOLDER_FAILURE,
  ] as const;
  const { folderId, name, actionName, actionPayload, limit, offset } =
    action.payload;

  const variables = {
    folderId,
    name: name.trim(),
    actionName,
    actionPayload,
    limit: limit || defaultSearchNameInFolderLimit,
    offset: offset || defaultSearchNameInFolderOffset,
  };
  const query = searchNameExistInFolderQuery;
  yield* put(
    Services.invokeGqlCall<SearchNameExistInFolderQueryResponse>(
      query,
      variables,
      actionTypes,
      { returnPartialDataOnNotFound: true }
    )
  );
}

function* searchNameExistsInFolderSuccess(
  // action: Action<
  //   { folder: FolderStateType },
  //   { variables: SearchNameExistsInFolderRequestPayload }
  // >
  action: ReturnType<typeof SEARCH_NAME_EXISTS_IN_FOLDER_SUCCESS>
) {
  const { folder } = action.payload;
  const { folderId, name, actionName, actionPayload, limit, offset } =
    action.meta.variables;

  // The results return all child folders at any level, so need to find the direct child folder
  const subfolders = (folder?.childFolders?.records || []).filter(
    (record) => record.parent.id === folderId
  );
  if (subfolders.length) {
    if (actionName === SearchNameActions.RESTORE) {
      const { id, folderId, name, parentId } =
        actionPayload as ActionRestoredFolderPayload;
      const actionTypes = [
        '',
        UPDATE_RESTORED_FOLDER_NAME_SUCCESS,
        UPDATE_RESTORED_FOLDER_NAME_FAILURE,
      ] as const;
      // VE-21114: under v2 rename by folder id (updateFolder is OLP-gated on the
      // id; treeObjectId returns not_allowed). Gated so v1 keeps its exact prior
      // behavior (rename by the id it was given).
      const isV2Folders = yield* select(selectV2FoldersEnabled);
      const variables = {
        // v1 renames by the id it was given; v2 by the folder id (falling back
        // to that same id when no distinct folder id is present).
        id: resolveFolderMutationId(isV2Folders, {
          folderId,
          treeObjectId: id,
        }),
        name: `${name} (restore)`,
      };
      const query = renameFolderQuery;
      yield* put(
        Services.invokeGqlCall<RenameFolderQueryResponse>(
          query,
          variables,
          actionTypes
        )
      );

      const { err } = yield* race({
        ok: take(UPDATE_RESTORED_FOLDER_NAME_SUCCESS),
        err: take(UPDATE_RESTORED_FOLDER_NAME_FAILURE),
      });

      if (err) {
        const intl = sagaIntl();
        yield* put(
          enqueueSnackbar({
            message: intl.formatMessage(
              {
                id: 'restoreFolderFailed',
                defaultMessage: 'Failed to restore the folder/request {name}.',
              },
              { name }
            ),
            variant: 'error',
          })
        );

        const ids = yield* select(selectFolderSelectedItems);
        yield* put(
          actionUpdateSelectedFolderItems({
            treeObjectId: ids.filter((i) => i !== id),
          })
        );
        return;
      }

      yield* moveRestoredFolderFromTrash(id, parentId, folderId);
      return;
    } else {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: `"${name}" - ${intl.formatMessage({
            id: 'nameExistsInFolder',
            defaultMessage:
              'This name already exist for this selected folder. Sorry, duplicate names not allowed.',
          })}`,
          variant: 'warning',
        })
      );
    }
  } else {
    const isFetchMoreAvailable = limit === subfolders?.length;
    if (isFetchMoreAvailable) {
      const nextOffset =
        (limit || defaultSearchNameInFolderLimit) +
        (offset || defaultSearchNameInFolderOffset);
      const searchPayload = {
        folderId,
        name,
        actionName,
        actionPayload,
        limit,
        offset: nextOffset,
      };
      yield* put(actionSearchNameExistsInFolderRequest(searchPayload));
    } else {
      if (actionName === SearchNameActions.ADD) {
        yield* put(
          actionCreateNewFolder(actionPayload as ActionCreateNewFolderPayload)
        );
      }
      if (actionName === SearchNameActions.RENAME) {
        yield* put(
          actionRenameFolder(actionPayload as ActionRenameFolderPayload)
        );
      }
      if (actionName === SearchNameActions.MOVE) {
        yield* put(
          actionMoveFolderRequest(actionPayload as ActionMoveFolderPayload)
        );
      }
      if (actionName === SearchNameActions.RESTORE) {
        const { id, parentId, folderId } =
          actionPayload as ActionRestoredFolderPayload;
        yield* moveRestoredFolderFromTrash(id, parentId, folderId);
      }
    }
  }
}

function* searchNameExistsInFolderFailure(
  // action: Action<
  //   [{ message: string }],
  //   { variables: SearchNameExistsInFolderRequestPayload }
  // >
  action: ReturnType<typeof SEARCH_NAME_EXISTS_IN_FOLDER_FAILURE>
) {
  const message = action.payload?.[0].message;
  const { name } = action.meta.variables;
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: `${intl.formatMessage({
        id: 'nameExistCheckFailed',
        defaultMessage: 'failed',
      })} "'${name}'". ${message}`,
      variant: 'error',
    })
  );
}

function* moveFileToCaseRequest(action: Action<ActionMoveFileToCasePayload>) {
  const actionTypes = [
    MOVE_FILE_TO_CASE_INIT,
    MOVE_FILE_TO_CASE_SUCCESS,
    MOVE_FILE_TO_CASE_FAILURE,
  ] as const;
  const query = moveTdoQuery;
  yield* put(
    Services.invokeGqlCall<MoveTdoQueryResponse>(
      query,
      action.payload,
      actionTypes
    )
  );
}

function* moveFileToCaseSuccess(
  // action: Action<
  //   { moveTemporalDataObject: { id: string } },
  //   { variables: ActionFileTdoWithCasePayload }
  // >
  action: ReturnType<typeof MOVE_FILE_TO_CASE_SUCCESS>
) {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'moveFileToCaseSuccess',
        defaultMessage: 'File moved successfully.',
      }),
      variant: 'success',
    })
  );
  const limit = yield* select(selectDefaultLimit);
  const offset = yield* select(selectDefaultOffset);
  const { oldFolderId } = action.meta.variables;
  if (oldFolderId) {
    yield* put(
      actionSetSelectedFolderSuccess({
        folderId: oldFolderId,
        limit,
        offset,
        isCase: true,
      })
    );
  }
}

function* moveFileToCaseFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'moveFileToCaseFailure',
        defaultMessage:
          'Unexpected error occurred while moving the file. Please try again later.',
      }),
      variant: 'error',
    })
  );
}

/**
 * The file's name for a lock refusal, or undefined when we hold nothing worth
 * showing. `AssetFileType` and the legacy row spell it differently, and the
 * legacy row's `tdoName` is SEEDED WITH THE TDO ID until its detail lands
 * (`unassigned.ts`) — which would print "1234 (ID 1234)".
 */
const lockRefusalFileName = (
  file: AssetFileType | LegacyMediaDetailType | undefined,
  tdoId: string
) => {
  const name = file && ('name' in file ? file.name : file.tdoName);
  return name && name !== tdoId ? name : undefined;
};

function* watchTdoInUse() {
  yield* takeLatest(TDO_IN_USE, function* (action) {
    yield* put(
      enqueueSnackbar({
        message: renderLockRefusal(sagaIntl(), action.payload),
        variant: 'warning',
        autoHideDuration: LOCK_REFRESH / 6,
      })
    );
  });
}

export function* fetchTdosRecursivelySaga(
  folderId: FolderId
): Generator<any, TDOForExport[], any> {
  const results: TDOForExport[] = [];
  const limit = 1000;

  const fetchFolderActionTypes = [
    'FETCH_EXPORT_FOLDER_INIT',
    'FETCH_EXPORT_FOLDER_SUCCESS',
    'FETCH_EXPORT_FOLDER_FAILURE',
  ] as const;

  // VE-21114: paginate sub-folders/TDOs — a single page silently truncated the
  // export at >1000 per node. Page until a response returns < limit records.
  const subFolders: { id: FolderId }[] = [];
  for (let offset = 0; ; offset += limit) {
    yield* put(
      Services.invokeGqlCall<FetchSubFoldersForExportQueryResponse>(
        fetchSubFoldersForExportQuery,
        { folderId, limit, offset },
        fetchFolderActionTypes
      )
    );
    const folderResult: {
      type: string;
      payload?: FetchSubFoldersForExportQueryResponse;
    } = yield* take([fetchFolderActionTypes[1], fetchFolderActionTypes[2]]);

    if (
      folderResult.type === fetchFolderActionTypes[2] ||
      !folderResult?.payload?.folder
    ) {
      // First page failing means the folder is unreadable → bail entirely; a
      // later page failing just stops pagination with what we already have.
      if (offset === 0) {
        return results;
      }
      break;
    }
    const records = folderResult.payload.folder.childFolders?.records ?? [];
    subFolders.push(...records);
    if (records.length < limit) {
      break;
    }
  }

  const fetchFilesActionTypes = [
    'FETCH_EXPORT_FILES_INIT',
    'FETCH_EXPORT_FILES_SUCCESS',
    'FETCH_EXPORT_FILES_FAILURE',
  ] as const;

  for (let offset = 0; ; offset += limit) {
    yield* put(
      Services.invokeGqlCall<FetchCaseContentsForExportQueryResponse>(
        fetchCaseContentsForExportQuery,
        { caseId: folderId, limit, offset },
        fetchFilesActionTypes
      )
    );
    const filesResult: {
      type: string;
      payload?: FetchCaseContentsForExportQueryResponse;
    } = yield* take([fetchFilesActionTypes[1], fetchFilesActionTypes[2]]);

    if (
      filesResult.type === fetchFilesActionTypes[2] ||
      !filesResult?.payload?.folder
    ) {
      break;
    }
    const tdos = filesResult.payload.folder.childTDOs?.records ?? [];
    results.push(...tdos);
    if (tdos.length < limit) {
      break;
    }
  }

  for (const subFolder of subFolders) {
    const tdos = yield* call(fetchTdosRecursivelySaga, subFolder.id);
    results.push(...tdos);
  }
  return results;
}

const buildExportRow = ({
  item,
  fields,
  isMainPage,
  orgName,
  isUnassignedMedia,
  isV2Folders = false,
}: ExportRowParams): ExportMediaListRow => {
  const row: ExportMediaListRow = {};

  const getStatus = (
    details: MainPageServices.TDOForExport['details']
  ): string => {
    const tags = Array.isArray(details?.tags) ? details.tags : [];
    const tag = (tags as { value?: string; redactionStatus?: string }[]).find(
      (t) => 'value' in t && t.value === 'in redaction'
    );
    return tag?.redactionStatus || '';
  };

  const getRedactedAsset = (
    exportItem: MainPageServices.TDOForExport
  ): {
    name?: string;
    modifiedDateTime?: string;
    details?: unknown;
  } => {
    if ('redactedMediaAssets' in exportItem) {
      return exportItem.redactedMediaAssets?.records?.[0] ?? {};
    }
    return {};
  };

  const parseRedactedAssetDetails = (
    details: unknown
  ): {
    hasAudioRedactions?: boolean;
    hasVideoRedactions?: boolean;
  } => {
    if (typeof details === 'string') {
      try {
        return JSON.parse(details) as {
          hasAudioRedactions?: boolean;
          hasVideoRedactions?: boolean;
        };
      } catch {
        return {};
      }
    }
    if (details && typeof details === 'object') {
      return details as {
        hasAudioRedactions?: boolean;
        hasVideoRedactions?: boolean;
      };
    }
    return {};
  };

  const redactedAsset = getRedactedAsset(item);
  const redactedAssetDetails = parseRedactedAssetDetails(
    redactedAsset?.details
  );
  const hasRedactedAsset = Boolean(redactedAsset?.name);

  // VE-21114: rebuild the full root→leaf chain (v2 drops the leaf from
  // folders[0].folderPath) before the positional column mapping / `> 3` check.
  const folderPath = tdoFolderChain(item.folders?.[0], isV2Folders);
  const sortedPath = folderPath.length ? sortFolderPath(folderPath) : [];
  const hasSubFolder = sortedPath.length > 3;

  if (fields.rootFolder) {
    row['Root Folder'] = isUnassignedMedia
      ? 'Unassigned Media'
      : orgName || sortedPath[0]?.name || '';
  }

  if (fields.folderLocation) {
    row['Folder'] = sortedPath[1]?.name || '';
    row['Sub Folder'] = (hasSubFolder ? sortedPath[2]?.name : '') || '';
    row['Request'] =
      (hasSubFolder ? sortedPath[3]?.name : sortedPath[2]?.name) || '';
  }

  if (fields.fileName) {
    row['Filename'] = item.name;
  }

  if (fields.fileId) {
    row['File Id'] = item.id;
  }

  if (fields.duration) {
    const hasDuration =
      !isExportImageTdo(item.details) &&
      item.startDateTime &&
      item.stopDateTime;
    row['Duration'] = hasDuration
      ? findDuration(item.startDateTime, item.stopDateTime)
      : '';
  }

  if (fields.createdDate) {
    row['Created Date'] = formatExportDateTime(item.createdDateTime);
  }

  if (fields.modifiedDate) {
    row['Modified Date'] = formatExportDateTime(item.modifiedDateTime);
  }

  if (fields.redactionName) {
    row['Redaction Name'] = isMainPage ? '' : redactedAsset?.name || '';
  }

  if (fields.hasAudioRedactions) {
    const value = redactedAssetDetails.hasAudioRedactions;
    row['Has Audio Redactions'] =
      value === undefined ? '' : String(value).toUpperCase();
  }

  if (fields.hasVideoRedactions) {
    let value = redactedAssetDetails.hasVideoRedactions;
    // Image and PDF redacted assets often omit this flag; infer TRUE when a
    // redacted file exists (a redacted PDF always has burned-in visual
    // redactions).
    if (
      value === undefined &&
      hasRedactedAsset &&
      (isExportImageTdo(item.details) || isExportPdfTdo(item.details))
    ) {
      value = true;
    }
    row['Has Visual Redactions'] =
      value === undefined ? '' : String(value).toUpperCase();
  }

  if (fields.fileStatus) {
    row['Status'] = getStatus(item.details);
  }

  return row;
};

const buildExportRowSafe = (params: ExportRowParams): ExportMediaListRow => {
  try {
    return buildExportRow(params);
  } catch (error) {
    console.error(
      'Export media list: failed to build row for',
      params.item.id,
      error
    );
    const row: ExportMediaListRow = {};
    if (params.fields.fileName) {
      row['Filename'] = params.item.name;
    }
    if (params.fields.fileId) {
      row['File Id'] = params.item.id;
    }
    return row;
  }
};

function* exportMediaList(
  action: ReturnType<typeof actionExportMediaListRequest>
) {
  const intl = sagaIntl();
  const loadingSnackbarKey = 'export-media-list-loading';

  try {
    yield* put(
      enqueueSnackbar({
        key: loadingSnackbarKey,
        message: intl.formatMessage({
          id: 'exportMediaListLoading',
          defaultMessage: 'Exporting media list...',
        }),
        variant: 'info',
        persist: true,
      })
    );

    const { selectedFolder, isRoot, fields, isMainPageView } = action.payload;

    const orgName = yield* select(selectOrganizationName);
    const orgId = yield* select(selectOrganizationId);
    const isV2Folders = yield* select(selectV2FoldersEnabled);
    const rootFolderToExport = yield* select(selectRootFolder);
    const folderToExport = isRoot ? rootFolderToExport : selectedFolder;

    const data: ExportMediaListRow[] = [];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    const getState = yield* select((s: any) => () => s);

    if (isMainPageView) {
      const allTDOs = yield* call(
        MainPageServices.fetchAllTDOsForExport,
        getState
      );

      data.push(
        ...allTDOs.map((tdo) =>
          buildExportRowSafe({
            item: tdo,
            fields,
            isMainPage: true,
            orgName,
            isV2Folders,
          })
        )
      );
    } else {
      if (!folderToExport?.id) {
        throw new Error('No folders selected');
      }

      const allTDOs = yield* call(fetchTdosRecursivelySaga, folderToExport.id);
      data.push(
        ...allTDOs.map((tdo) =>
          buildExportRowSafe({
            item: tdo,
            fields,
            isMainPage: false,
            orgName,
            isV2Folders,
          })
        )
      );

      if (isRoot) {
        const legacyTDOs = yield* call(
          MainPageServices.fetchAllTDOsForExport,
          getState
        );

        data.push(
          ...legacyTDOs.map((tdo) =>
            buildExportRowSafe({
              item: tdo,
              fields,
              isMainPage: false,
              orgName,
              isUnassignedMedia: true,
              isV2Folders,
            })
          )
        );
      }
    }

    if (data.length === 0) {
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'exportMediaListNoFiles',
            defaultMessage:
              'No files found inside the selected folder/request!',
          }),
          variant: 'warning',
        })
      );
      return;
    }

    const csv = Papa.unparse(data);
    const exportedAt = new Date().toISOString().split('.')[0] + 'Z'; // remove ms

    let filename = `Redact Media List ${exportedAt}.csv`;

    if (isMainPageView) {
      filename = `Redact Media List [${orgName} (${orgId})] [Full Report] ${exportedAt}.csv`;
    } else if (isRoot && rootFolderToExport) {
      filename = `Redact Media List [${orgName} (${orgId})] [Full Report] ${exportedAt}.csv`;
    } else if (folderToExport) {
      const folderName = folderToExport.name ?? 'Selected Folder';
      filename = `Redact Media List [${orgName} (${orgId})] [${folderName}] ${exportedAt}.csv`;
    }

    const element = document.createElement('a');
    const file = new Blob([csv], {
      type: 'text/csv',
    });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    yield* put({
      type: EXPORT_MEDIA_LIST_SUCCESS,
    });
  } catch (error) {
    console.error('Export media list error:', error);

    yield* put({
      type: EXPORT_MEDIA_LIST_FAILURE,
      payload: error,
    });
  } finally {
    yield* put(closeSnackbar(loadingSnackbarKey));
  }
}

function* exportMediaListSuccess() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'exportMediaListSuccess',
        defaultMessage: 'Media list exported successfully',
      }),
      variant: 'success',
    })
  );
}

function* exportMediaListFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'exportMediaListFailure',
        defaultMessage: 'Failed to export media list. Please try again.',
      }),
      variant: 'error',
    })
  );
}

function* watchExportMediaList() {
  yield* all([
    takeLatest(EXPORT_MEDIA_LIST_REQUEST, exportMediaList),
    takeLatest(EXPORT_MEDIA_LIST_SUCCESS, exportMediaListSuccess),
    takeLatest(EXPORT_MEDIA_LIST_FAILURE, exportMediaListFailure),
  ]);
}
